import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import SELogo from "./SE_Logo_White_RGB.png";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import GroupIcon from "@mui/icons-material/Group";
import Autocomplete from "@mui/material/Autocomplete";
import { Container, Typography, TextField, Button, Box, Collapse, Paper, Grid, Checkbox, FormControlLabel, CircularProgress, Snackbar, Alert, Avatar, Menu, MenuItem, ListItemIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import { FinancialDetailsTable } from "./FinancialDetailsTable";
import { PrimaryButton, SecondaryButton, DangerButton, DangerOutlineButton, DeleteIconButton, EditIconButton } from "./components/AppButtons";
import { MigrationBanner, isMigrationModeEnabled } from "./components/MigrationBanner";
import './App.css'
import { fetchRegions, getExistingSupplier, fetchFTerms, addSupplier, fetchAllSuppliers, fetchAllSuppliersbyCommodityId, fetchAllCommodities, fetchSupplierStatuses, fetchIntegratedOfferings, fetchProductionLocations, fetchProducts, addProduct, fetchCommodities, addCommodity, fetchCommodityManagers, addCommodityManager, updateCommodityManagerAdmin, UpdateSupplier, deleteSupplier, deleteCommodityManager, fetchParseDataSuppliers, fetchParseDataSuppliersByIfas, fetchParseDataFinancialDetails, PARSE_DATA_MIN_SEARCH_LENGTH, fetchPreferenceStatuses, checkAuthorizedUser, initializeAxios, isAxiosInitialized, ParseDataSupplier, ParseDataFinancialDetail, PreferenceStatus } from "./ApiService";
import { useAuth } from "./authContext";
import { AuthProvider } from "./authContext";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";

type CommodityManagerOption = {
  mail: string;
  name: string;
  isSuperUser: boolean;
};

const normalizeCommodityManager = (value: any): CommodityManagerOption | null => {
  const isEmail = (input: string) => /^\S+@\S+\.\S+$/.test(input);

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? { mail: trimmed, name: '', isSuperUser: false } : null;
  }

  if (!value || typeof value !== 'object') return null;

  const rawMail = String(value.mail ?? value.Mail ?? value.email ?? value.Email ?? '').trim();
  const rawName = String(value.name ?? value.Name ?? value.displayName ?? value.DisplayName ?? '').trim();
  const rawIsSuperUser =
    value.ISSUPERUSER ??
    value.isSuperUser ??
    value.isSuperuser ??
    value.IsSuperUser ??
    value.IsSuperuser ??
    value.issuperuser ??
    value.IS_SUPER_USER ??
    value.is_super_user;

  const normalizedIsSuperUser = typeof rawIsSuperUser === 'string'
    ? rawIsSuperUser.trim().toLowerCase()
    : rawIsSuperUser;

  const isSuperUser =
    normalizedIsSuperUser === true ||
    normalizedIsSuperUser === 1 ||
    normalizedIsSuperUser === '1' ||
    normalizedIsSuperUser === 'true' ||
    normalizedIsSuperUser === 'yes' ||
    normalizedIsSuperUser === 'y';

  // Keep email strictly in email column, even if backend sends it in name/displayName.
  const mail = isEmail(rawMail) ? rawMail : (isEmail(rawName) ? rawName : '');
  const name = isEmail(rawName) ? '' : rawName;
  const resolvedName = name || String(value.name ?? value.Name ?? '').trim();

  if (!mail) return null;
  return { mail, name: resolvedName, isSuperUser };
};

const getCommodityManagerMail = (value: any): string => normalizeCommodityManager(value)?.mail ?? '';
const getCommodityManagerName = (value: any): string => {
  const normalized = normalizeCommodityManager(value);
  return normalized?.name ?? '';
};
const getCommodityManagerIsSuperUser = (value: any): boolean => normalizeCommodityManager(value)?.isSuperUser ?? false;

const getCommodityManagerDisplayName = (value: any): string => {
  const normalized = normalizeCommodityManager(value);
  if (!normalized) return '';
  return normalized.name || normalized.mail;
};

const getSupplierOptionId = (supplier: any): number => {
  if (!supplier) return 0;
  const id = supplier.supplierID ?? supplier.SupplierID ?? supplier.id ?? supplier.ID ?? 0;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getSupplierOptionLabel = (supplier: any): string =>
  String(supplier?.name ?? supplier?.Name ?? '');

const normalizePreferenceStatusId = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const parseLeadTimeValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && Number.isInteger(parsed) ? parsed : null;
};

const formatLeadTimeValue = (value: unknown): string => {
  const parsed = parseLeadTimeValue(value);
  return parsed === null ? '' : String(parsed);
};

const getProductOptionId = (product: any): number => {
  if (!product) return 0;
  const id = product.portfolioID ?? product.productID ?? product.id ?? product.ID ?? 0;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getProductOptionLabel = (product: any): string =>
  String(product?.portfolio ?? product?.name ?? product?.Name ?? '');

const isSameProductOption = (a: any, b: any) =>
  getProductOptionId(a) > 0 && getProductOptionId(a) === getProductOptionId(b);

const AppContent = () => {

  // Controlled fields for TextFields

  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [supplierId, setsupplierId] = useState(0);
  const [commodityId, setCommodityId] = useState(0);
  const [selectedFilterCommodity, setSelectedFilterCommodity] = useState<any | null>(null);
  const [selectedFilterSupplier, setSelectedFilterSupplier] = useState<any | null>(null);
  const [name, setSupplierName] = useState("");
  const [details, setportfolioDeatils] = useState("");
  const [folderLink, setSupplierFolderLink] = useState("");
  const [logo, setLogoUrl] = useState("");
  const [paymentTerms, setPaymentTerms] = useState(0);
  const [mpaLink, setMpaLink] = useState("");
  const [selectedProductCommodity, setSelectedProductCommodity] = useState<any | null>(null);
  const [fTermsOptions, setFTermsOptions] = useState<any[]>([]);
  const [selectedFTerm, setSelectedFTerm] = useState<any | null>(null);
  // const [newFTerm, setNewFTerm] = useState("");
  const [selectedRegions, setSelectedRegions] = useState<any[]>([]);
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [contactRegionOptions, setContactRegionOptions] = useState<any[]>([]);
  const [Suppliers, setAllSuppliers] = useState<any[]>([]);
  const [Commodities, setAllCommodities] = useState<any[]>([]);

  const [errorMessage, setErrorMessageSN] = useState('');
  const [isDeleted, setisDeleted] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = React.useState<CommodityObj | string | number | null>(null);
  const [dialogboxopen, setdialogboxopen] = useState(false);



  const commodityLabel = (x: CommodityObj | string | number | null) =>
    x == null ? "" : typeof x === "object" ? (x.commodity ?? x.commodityID ?? "") : String(x)



  //const [isEmailValid, setIsEmailValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState<Record<number, boolean>>({});


  useEffect(() => {
    ;
  }, [showUpdateButton]);
  // Contract Type checkboxes
  const [contractTypes, setContractTypes] = useState({
    masterPurchasingAgreementMPA: false,
    nonDisclosureAgreement: false,
    project: false,
  });
  // Qualification Status checkboxes
  const [qualificationStatus, setQualificationStatus] = useState({
    qualificationStatus_NotQualified: false,
    qualificationStatus_ReadyforBusiness_R4B: false,
    qualificationStatus_ReadytoOrder_R4O: false,
  });
  // Supplier model POST API
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);


  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);
  
  // Authentication
  const { isAuthenticated, account, login, logout, getAccessToken } = useAuth();
  
  // Check for authentication errors in URL (from redirect) or sessionStorage
  useEffect(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('Authentication error from URL:', error, errorDescription);
      
      // Check for AADSTS650057
      if (error === 'invalid_client' || errorDescription?.includes('AADSTS650057')) {
        setSnackbar({
          open: true,
          message: 'Authentication configuration issue detected. Some users may experience login issues. Please contact your administrator. Error: AADSTS650057',
          severity: 'warning'
        });
        // Clear error from URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setSnackbar({
          open: true,
          message: `Authentication error: ${errorDescription || error}. Please try again.`,
          severity: 'error'
        });
        // Clear error from URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else {
      // Check sessionStorage for stored errors
      try {
        const storedError = sessionStorage.getItem('auth_error');
        if (storedError) {
          const errorData = JSON.parse(storedError);
          // Only show if error is recent (within last 5 minutes)
          if (Date.now() - errorData.timestamp < 5 * 60 * 1000) {
            if (errorData.code === 'AADSTS650057') {
              setSnackbar({
                open: true,
                message: 'Authentication configuration issue detected. Some users may experience login issues. Please contact your administrator. Error: AADSTS650057',
                severity: 'warning'
              });
            } else {
              setSnackbar({
                open: true,
                message: `Authentication error: ${errorData.message}. Please try again.`,
                severity: 'error'
              });
            }
            // Clear stored error after displaying
            sessionStorage.removeItem('auth_error');
          } else {
            // Remove stale errors
            sessionStorage.removeItem('auth_error');
          }
        }
      } catch (e) {
        console.warn('Failed to read stored auth error:', e);
      }
    }
  }, []);
  
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };
  
  // Debug logging
  useEffect(() => {
    console.log('AppContent - isAuthenticated:', isAuthenticated);
    console.log('AppContent - account:', account);
    if (account) {
      console.log('Account properties:', Object.keys(account));
      console.log('Account name:', account.name);
      console.log('Account username:', account.username);
      console.log('Account idTokenClaims:', account.idTokenClaims);
    }
  }, [isAuthenticated, account]);
  
  // Initialize axios with token getter when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('Initializing axios with token getter');
      initializeAxios(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  const ACCESS_DENIED_KEY = 'supplierTool.accessDeniedMessage';
  const defaultAccessDeniedMessage =
    'Access denied. Only users listed as Commodity Managers can use this application.';

  const [accessCheckLoading, setAccessCheckLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(
    () => Boolean(sessionStorage.getItem(ACCESS_DENIED_KEY))
  );
  const [accessDeniedMessage, setAccessDeniedMessage] = useState(
    () => sessionStorage.getItem(ACCESS_DENIED_KEY) || defaultAccessDeniedMessage
  );

  const handleAccessDenied = useCallback(
    (message?: string) => {
      const msg = message || defaultAccessDeniedMessage;
      sessionStorage.setItem(ACCESS_DENIED_KEY, msg);
      setAccessDeniedMessage(msg);
      setAccessDenied(true);
      setAccessCheckLoading(false);
      // Auto sign-out so the Azure AD session is cleared
      logout();
    },
    [logout]
  );

  const handleTryAccessAgain = useCallback(async () => {
    sessionStorage.removeItem(ACCESS_DENIED_KEY);
    setAccessDenied(false);
    setAccessDeniedMessage(defaultAccessDeniedMessage);
    try {
      await login();
    } catch (error) {
      console.error('Try again login failed:', error);
      setSnackbar({
        open: true,
        message: 'Login failed. Please try again.',
        severity: 'error',
      });
    }
  }, [login]);

  // After Azure AD login, allow only Commodity Managers
  useEffect(() => {
    let cancelled = false;

    const verifyAccess = async () => {
      if (!isAuthenticated || !account) {
        setAccessCheckLoading(false);
        return;
      }

      // Clear any previous denial once a fresh authenticated session starts verifying
      sessionStorage.removeItem(ACCESS_DENIED_KEY);
      setAccessDenied(false);
      setAccessCheckLoading(true);
      initializeAxios(getAccessToken);

      try {
        let result = await checkAuthorizedUser();
        if (!result.authorized && result.message?.includes('Unable to verify')) {
          await new Promise((r) => setTimeout(r, 800));
          result = await checkAuthorizedUser();
        }

        if (cancelled) return;

        if (!result.authorized) {
          handleAccessDenied(
            result.message || defaultAccessDeniedMessage
          );
        } else {
          sessionStorage.removeItem(ACCESS_DENIED_KEY);
          setAccessDenied(false);
          setAccessCheckLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          handleAccessDenied('Unable to verify access. Please try again or contact your administrator.');
        }
      }
    };

    void verifyAccess();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account, getAccessToken, handleAccessDenied]);

  type Country = { countryID: number | string | null; longName: string | null };
  type ProductObj = { portfolio?: string | null; portfolioID?: number | string | null } | string | null;
  type CommodityObj = {
    commodity?: string | null;
    commodityID?: number | string | null;
    preferenceStatusID?: number | null;
    preferenceStatus?: string | null;
  } | string | null;

  const commodityKey = useCallback((c: CommodityObj | string | number | null | undefined) => {
    if (c == null) return null;
    return typeof c === "object" ? (c.commodityID ?? c.commodity) : c;
  }, []);


  type PortfolioRow = {
    product?: ProductObj;
    productID?: number | string | null;
    productCommodity?: CommodityObj;
    productCommodityID?: number | string | null;
    countries?: Country[] | (string | null)[] | null;
    leadTimeMonths?: number | null;
    leadTimeWeeks?: number | null;

  };

  const safeString = (v: any) => (typeof v === 'string' ? v : v?.toString?.() ?? '');

  const normalizeCountries = (arr: any): Country[] =>
    (Array.isArray(arr) ? arr : []).map((c: any) => ({
      countryID: c?.countryID ?? null,
      longName:
        (typeof c === 'string' ? c : c?.longName) ?? null,
    }));

  const getProductId = (row: PortfolioRow) =>
    (typeof row.product === 'object' && row.product ? row.product.portfolioID : undefined) ??
    row.productID ?? null;

  const getProductName = (row: PortfolioRow) =>
    (typeof row.product === 'object' && row.product ? row.product.portfolio : undefined) ??
    (typeof row.product === 'string' ? row.product : null);

  const getCommodityId = (row: PortfolioRow) =>
    (typeof row.productCommodity === 'object' && row.productCommodity ? row.productCommodity.commodityID : undefined) ??
    row.productCommodityID ?? null;

  const getCommodityName = (row: PortfolioRow) =>
    (typeof row.productCommodity === 'object' && row.productCommodity ? row.productCommodity.commodity : undefined) ??
    (typeof row.productCommodity === 'string' ? row.productCommodity : null);





  function mergeByProductAndCommodity(rows: PortfolioRow[]): PortfolioRow[] {
    const acc = new Map<string, PortfolioRow>();

    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const productID = getProductId(row);
      const productName = getProductName(row);
      const commodityID = getCommodityId(row);
      const commodityName = getCommodityName(row);

      const key = `${safeString(productID)}::${safeString(productName)}::${safeString(commodityID)}::${safeString(commodityName)}`;

      const normalizedCountries = normalizeCountries(row.countries);

      if (!acc.has(key)) {
        // Seed the consolidated row; ensure product/commodity are in object form
        const productObj =
          typeof row.product === 'object' && row.product
            ? row.product
            : { portfolio: productName, portfolioID: productID };

        const commodityObj =
          typeof row.productCommodity === 'object' && row.productCommodity
            ? row.productCommodity
            : { commodity: commodityName, commodityID: commodityID };

        acc.set(key, {
          ...row,
          leadTimeMonths: parseLeadTimeValue(row.leadTimeMonths ?? (row as any).LeadTimeMonths),
          leadTimeWeeks: parseLeadTimeValue(row.leadTimeWeeks ?? (row as any).LeadTimeWeeks),
          product: productObj,
          productID: productObj?.portfolioID ?? productID,
          productCommodity: commodityObj,
          productCommodityID: commodityObj?.commodityID ?? commodityID,
          countries: normalizedCountries.filter(c => c.countryID !== null || c.longName !== null),
        });
      } else {
        // Merge countries and de‑duplicate by countryID or longName
        const existing = acc.get(key)!;
        const merged = [
          ...(existing.countries as Country[] ?? []),
          ...normalizedCountries,
        ];

        const deduped = merged.filter((co, i, arr) => {
          const k = co.countryID ?? co.longName;
          return k ? arr.findIndex(x => (x.countryID ?? x.longName) === k) === i : false;
        });

        acc.set(key, {
          ...existing,
          countries: deduped,
          leadTimeMonths: existing.leadTimeMonths ?? row.leadTimeMonths ?? null,
          leadTimeWeeks: existing.leadTimeWeeks ?? row.leadTimeWeeks ?? null,
        });
      }
    });

    return Array.from(acc.values());
  }

  useEffect(() => { if (supplierId) { setisDeleted(false); } }, [supplierId]);

  const isFormValid = () => {
    return (
      name.trim()
    );
  };

  // validation
  const isValidUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);

      // Check protocol
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return false;
      }

      // Regex for stricter validation (domain + optional path/query)
      const urlRegex = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

      return urlRegex.test(url);
    } catch {
      return false;
    }
  };

  const isValidEmail = (value?: string): boolean => {
    if (!value) return true; // treat empty as invalid if required
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  type Contact = { email: string };


  const validateAllContactEmails = (
    contacts: Contact[],
    setSnackbar: (s: { open: boolean; message: string; severity: 'error' | 'success' | 'warning' | 'info' }) => void
  ): boolean => {
    for (const contact of contacts) {
      if (!isValidEmail(contact?.email)) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid email address in contact details',
          severity: 'error',
        });
        return false; // abort on first invalid
      }
    }
    return true; // all valid
  };



  function handleDeleteCommodity(
    target: CommodityObj | string | number,
    opts?: { closeConfirm?: () => void }
  ) {
    const key = commodityKey(target);

    setSelectedCommodities((prev: Array<CommodityObj | string | number>) =>
      prev.filter((c) => commodityKey(c) !== key)
    );


    setProductPortfolio((prev: Array<any>) =>
      prev.filter((item) => commodityKey(item?.productCommodity) !== key)
    );


    setContacts((prev: Array<any>) =>
      prev.map((contact) => {
        const current = contact?.commodity;

        // Normalize to array
        const arr = Array.isArray(current)
          ? current
          : current == null
            ? []
            : [current];

        // Filter out the deleted commodity by key
        const filtered = arr.filter((item) => commodityKey(item) !== key);

        return { ...contact, commodity: filtered };
      })
    );



    opts?.closeConfirm?.();
  }




  const handleSaveSupplier = async () => {
    // Validate mandatory fields
    if (name === "") {
      setSnackbar({
        open: true,
        message: 'Please fill supplier name. Supplier name is required',
        severity: 'error'
      });
      return;
    }

    if (errorMessage !== "") {

      setSnackbar({
        open: true,
        message: 'Please check supplier name error and fix it',
        severity: 'error'
      });
      return;

    }



    if (!isValidUrl(folderLink) && folderLink !== "") {
      setSnackbar({
        open: true,
        message: 'Please enter valid supplier folder link',
        severity: 'error'
      });
      return;
    }

    if (!isValidUrl(logo) && logo !== "") {
      setSnackbar({
        open: true,
        message: 'Please enter valid logo URL ',
        severity: 'error'
      });
      return;
    }

    if (!isValidUrl(mpaLink) && mpaLink !== "") {
      setSnackbar({
        open: true,
        message: 'Please enter valid MPA link under supplier details',
        severity: 'error'
      });
      return;
    }

    if (!validateAllContactEmails(contacts, setSnackbar)) {
      return; // abor
    }



    const hasUnaddedManager = commodityManagerRows.some((row, idx) => {
      return !validationErrors[idx] && row.manager && !hasCommodityManagerMail(row.manager);
    });

    if (hasUnaddedManager) {
      setSnackbar({
        open: true,
        message: 'Please add all Commodity Managers before saving',
        severity: 'error'
      });
      return;
    }
    //commodity button validation


    const isAddButtonEnabled = commodityInput.trim() &&
      !commodityOptions.some(c => c.commodity === commodityInput.trim());

    if (isAddButtonEnabled) {
      setSnackbar({
        open: true,
        message: 'Please add or clear the commodity input before saving.',
        severity: 'error'
      });
      return;
    }


    const isCommodityAddPending = selectedCommodity &&
      !selectedCommodities.some(c =>
        isCommodityObject(c) && isCommodityObject(selectedCommodity)
          ? c.commodityID === selectedCommodity.commodityID
          : c === selectedCommodity
      );

    if (isCommodityAddPending) {
      setSnackbar({
        open: true,
        message: 'Please ensure commodity has been selected before saving',
        severity: 'error'
      });
      return; // Stop save
    }

    // add button validation under product



    const isAddProductButtonEnabled = () => {
      const trimmed = newProduct.trim();
      return (
        trimmed.length > 0 &&
        !selectedProducts.some((product) => getProductOptionLabel(product).toLowerCase() === trimmed.toLowerCase())
      );
    };
    if (isAddProductButtonEnabled()) {
      setSnackbar({
        open: true,
        message: 'Please add the product or clear the input before saving.',
        severity: 'error'
      });
      return;
    }
    //select button validation for product


    const isSelectProductButtonEnabled = () => {
      return selectedProducts.length > 0 && selectedCountries.length > 0;
    };



    if (isSelectProductButtonEnabled()) {
      setSnackbar({
        open: true,
        message: 'Please complete product selection before saving.',
        severity: 'error'
      });
      return;
    }


    //     const hasInvalidRowCommodity = commodityManagerRows.some(row => {
    //   const managerFilled = typeof row.manager === 'string' && row.manager.trim() !== '';
    //   const emailFilled = typeof row.email === 'string' && row.email.trim() !== '';
    //   const locationFilled = row.location !== null && row.location !== undefined;

    //   if (row.global) {
    //     return !(managerFilled && emailFilled); // Global requires manager & email
    //   }

    //   // If manager is empty (Add button disabled), only location is required
    //   if (!managerFilled) {
    //     return !locationFilled; // Invalid if location missing
    //   }

    //   // If manager is filled → require all three
    //   const filledCount = [managerFilled, emailFilled, locationFilled].filter(Boolean).length;
    //   return filledCount < 3; // Invalid if partially filled
    // });

    // if (hasInvalidRowCommodity) {
    //   setSnackbar({
    //     open: true,
    //     message: 'Please fill all details under Commodity Managers section',
    //     severity: 'error'
    //   });
    //   return; // Stop save
    // }





    // const hasIncompleteRowFinancialDetails = financialRows.some(row => {
    //   const fyFilled = typeof row.fy === 'string' && row.fy.trim() !== '';
    //   const valueFilled = typeof row.value === 'string' && row.value.trim() !== '';
    //   const emailFilled = typeof row.email === 'string' && row.email.trim() !== '';
    //   const periodFilled = typeof row.period === 'string' && row.period.trim() !== '';

    //   const filledCount = [fyFilled, valueFilled, emailFilled, periodFilled].filter(Boolean).length;

    //   return filledCount > 0 && filledCount < 4; // Partially filled
    // });

    // if (hasIncompleteRowFinancialDetails) {
    //   setSnackbar({
    //     open: true,
    //     message: 'Please fill all the details under Financial Details',
    //     severity: 'error'
    //   });
    //   return; // Stop save
    // }




    const hasIncompleteContactRow = contacts.some(row => {
      const nameFilled = typeof row.name === 'string' && row.name.trim() !== '';
      const surnameFilled = typeof row.surname === 'string' && row.surname.trim() !== '';
      const emailFilled = typeof row.email === 'string' && row.email.trim() !== '';
      const jobRoleFilled = typeof row.jobRole === 'string' && row.jobRole.trim() !== '';

      // ✅ Guard with Array.isArray, then safely cast to unknown[]
      const commodityArray: unknown[] = Array.isArray(row.commodity) ? (row.commodity as unknown[]) : [];
      const regionsArray: unknown[] = Array.isArray(row.regions) ? (row.regions as unknown[]) : [];

      const commodityFilled = commodityArray.length > 0;
      const regionsFilled = regionsArray.length > 0; // Multiple regions

      const filledCount = [nameFilled, surnameFilled, emailFilled, jobRoleFilled, commodityFilled, regionsFilled]
        .filter(Boolean).length;

      return filledCount > 0 && filledCount < 6;
    }) // Partially filled

    if (hasIncompleteContactRow) {
      setSnackbar({
        open: true,
        message: 'Please fill all the details under Contact Details',
        severity: 'error'
      });
      return; // Stop save
    }











    setSaving(true);
    setSnackbar({ open: false, message: '', severity: 'success' });


    // Build supplier model
    const supplierModel = {
      SupplierID: supplierId,
      name,
      folderLink,
      logo,
      details,
      financialDetails: (Array.isArray(financialRows) ? financialRows : []).filter(row => (fy => fy !== null && fy !== undefined && String(fy).trim() !== '')(row?.fy)).map(row => ({
        fy: row?.fy ?? null,
        value: row?.value ?? null,
        period: row?.period ?? null,
        commodityID: row?.commodityID != null && row.commodityID > 0 ? row.commodityID : null,
        commodity: row?.commodity ?? null,
        pov: !!row?.pov,
        id: typeof row?.id === 'number' && row.id > 0 ? row.id : null,
        supplierIFA: row?.supplierIFA?.trim() || null,

      })),

      commodities: (Array.isArray(selectedCommodities) ? selectedCommodities : []).map(c => ({
        commodityID: isCommodityObject(c) ? c.commodityID : null,
        commodity: isCommodityObject(c) ? c.commodity : c ?? null,
        preferenceStatusID: isCommodityObject(c) ? normalizePreferenceStatusId(c.preferenceStatusID) : null,
      })),

      productPortfolio: (Array.isArray(productPortfolio) ? productPortfolio : []).map(pp => ({
        productID: pp?.product?.portfolioID ?? pp?.product?.productID ?? null,
        product: pp?.product?.portfolio ?? pp?.product ?? null,
        countries: (Array.isArray(pp?.countries) ? pp.countries : []).map((c) => ({
          countryID: c?.countryID ?? null,
          longName: c?.longName ?? c ?? null,
        })),
        productCommodityID: pp?.productCommodity?.commodityID ?? pp?.productCommodity?.productCommodityID ?? null,
        productCommodity: pp?.productCommodity?.commodity ?? pp?.productCommodity ?? null,
        commodityID: pp?.productCommodity?.commodityID ?? null,
        leadTimeMonths: parseLeadTimeValue(pp?.leadTimeMonths),
        leadTimeWeeks: parseLeadTimeValue(pp?.leadTimeWeeks),
      })),

      supplierClassificationRegions: (Array.isArray(selectedRegions) ? selectedRegions : []).map(r => ({
        regionID: r?.regionID ?? null,
        region: r?.region ?? r ?? null,
      })),

      contractTypes,

      terms: {
        paymentTerms,
        fTermID: selectedFTerm?.f_TermsID ?? null,
      },

      mpa: {
        link: mpaLink,
        statusID: selectedStatus?.supplierStatusID ?? null,
      },

      qualificationStatus,
      integratedOfferings: Array.isArray(selectedIntegratedOfferings) ? selectedIntegratedOfferings : [],

      contacts: (Array.isArray(contacts) ? contacts : []).map(c => ({
        contactID: c?.contactID ?? 0,
        name: c?.name ?? '',
        surname: c?.surname ?? '',
        email: c?.email ?? '',
        jobRole: c?.jobRole ?? '',
        commodity: Array.isArray(c?.commodity) ? c.commodity : [],
        regions: Array.isArray(c?.regions) ? c.regions : [],
      })),

      commodityManagers: Array.isArray(commodityManagerRows) ? commodityManagerRows : [],
    };


    console.log('Final supplier model:', supplierModel);

    try {
      if (showUpdateButton) {
        console.log(supplierModel);
        supplierModel.commodityManagers = supplierModel.commodityManagers?.filter(x => x.manager !== null && x.manager !== "");
        supplierModel.contacts = supplierModel.contacts.filter(x => x.name?.trim() !== "" && x.name?.trim() !== null);
        const response = await UpdateSupplier(supplierModel);
        if (response === "") {
          console.log(response);
          setSnackbar({ open: true, message: 'Supplier Updated successfully!', severity: 'success' });
          setShowDetailsScreen(false);
          void Promise.all([fetchAllSuppliers(), fetchAllCommodities()]).then(([suppliers, commodities]) => {
            if (Array.isArray(suppliers) && suppliers.length > 0) {
              setAllSuppliers(suppliers);
            }
            if (Array.isArray(commodities) && commodities.length > 0) {
              setAllCommodities(commodities);
            }
          });
        }
        else {
          console.log(response);
          setSnackbar({ open: true, message: response, severity: 'error' });
        }
      }
      else {
        supplierModel.commodityManagers = supplierModel.commodityManagers?.filter(x => x.manager !== null && x.manager.trim() !== "");
        supplierModel.contacts = supplierModel.contacts.filter(x => x.name?.trim() !== "" && x.name?.trim() !== null);
        const response = await addSupplier(supplierModel);
        if (response === "") {
          console.log(response);
          setSnackbar({ open: true, message: 'Supplier Created successfully!', severity: 'success' });
          setShowDetailsScreen(false);
          void Promise.all([fetchAllSuppliers(), fetchAllCommodities()]).then(([suppliers, commodities]) => {
            if (Array.isArray(suppliers) && suppliers.length > 0) {
              setAllSuppliers(suppliers);
            }
            if (Array.isArray(commodities) && commodities.length > 0) {
              setAllCommodities(commodities);
            }
          });
        }
        else {
          console.log(response);
          setSnackbar({ open: true, message: response, severity: 'error' });
        }
      }

      console.log('Supplier model to send:', supplierModel);
    } catch (err) {
      const apiMessage = err instanceof Error ? err.message : '';
      setSnackbar({
        open: true,
        message: apiMessage || (showUpdateButton
          ? 'Error updating supplier. Please try again.'
          : 'Error saving supplier. Please try again.'),
        severity: 'error'
      });
      console.error(
        showUpdateButton ? 'Error updating supplier:' : 'Error saving supplier:',
        err
      );
    } finally {
      setSaving(false);
    }
    setSearchValue("");
  };
  const handleDeleteSupplier = async () => {
    try {
      setLoading(true);
      const response = await deleteSupplier(supplierId);
      if (response) {
        console.log(response);
        // Handle successful deletion (e.g., show a message, refresh list)
        setLoading(true);
        setSnackbar({ open: true, message: 'Supplier Deleted successfully!', severity: 'success' });
        setShowDetailsScreen(false);
        setisDeleted(true);
        setLoading(false);
        window.location.reload();
        setsupplierId(0);
        setCommodityId(0);
        const [suppliers] = await Promise.all([fetchAllSuppliers()]);
        if (Array.isArray(suppliers) && suppliers.length > 0) {
          setAllSuppliers(suppliers);
        }

      } else {
        console.log(response);
        // Handle error
        setLoading(false);
        setSnackbar({ open: true, message: 'Error deleting supplier. Please try again.', severity: 'error' });
      }
    } catch (error) {
      setLoading(false);
      console.error('Error deleting supplier:', error);
    }
  };





  const handleExistingSupplier = async (supplierID: any) => {
    try {
      setLoading(true);
      const supplierData = await getExistingSupplier(supplierID);
      if (!supplierData) {
        setSnackbar({ open: true, message: 'Supplier profile could not be loaded.', severity: 'error' });
        return;
      }
      setsupplierId(supplierData.supplierID ?? supplierData.SupplierID ?? 0);
      setSupplierName(supplierData.name || "");
      setSupplierFolderLink(supplierData.folderLink || "");
      setLogoUrl(supplierData.logo || "");
      setportfolioDeatils(supplierData.details || "");
      setContractTypes(supplierData.contractTypes || {});
      const mappedFinancialRows =
        supplierData.financialDetails?.length > 0
          ? supplierData.financialDetails.map((fd: any, index: number) => {
              const commodityID = fd.commodityID ?? fd.CommodityID ?? null;
              const commodityFromApi = String(fd.commodity ?? fd.Commodity ?? '').trim();
              const commodityFromOptions = commodityID
                ? commodityOptions.find((c) => c.commodityID === commodityID)?.commodity?.trim() ?? ''
                : '';
              return {
              id: fd.id ?? Date.now() + index,
              fy: fd.fy ?? fd.fY ?? '',
              period: fd.period ?? '',
              value: fd.value ?? '',
              pov: !!fd.pov,
              pvo: fd.pvo != null ? !!fd.pvo : !fd.pov,
              commodityID,
              commodity: commodityFromApi || commodityFromOptions,
              supplierIFA: fd.supplierIFA ?? fd.SupplierIFA ?? null,
            };
            })
          : [];
      setFinancialRows(mappedFinancialRows);
      setFinancialFilterResetKey((key) => key + 1);
      await restoreParseSuppliersFromFinancialRows(mappedFinancialRows);
      setSelectedCommodities(
        supplierData.commodities?.length > 0
          ? supplierData.commodities.map((c: any) => ({
              commodityID: c.commodityID ?? c.CommodityID ?? null,
              commodity: c.commodity ?? c.Commodity ?? '',
              preferenceStatusID: normalizePreferenceStatusId(c.preferenceStatusID ?? c.PreferenceStatusID),
              preferenceStatus: normalizePreferenceStatusId(c.preferenceStatusID ?? c.PreferenceStatusID)
                ? (c.preferenceStatus ?? c.PreferenceStatus ?? '')
                : '',
            }))
          : []
      );
      // Map productPortfolio to include full product object and productID
      const merged = mergeByProductAndCommodity(supplierData.productPortfolio ?? []);
      setProductPortfolio(
        merged.length > 0
          ? merged.map((item: any) => {
            const matchedProduct = productOptions.find(
              (p: any) => p.portfolio === item.product || p.portfolioID === item.productID
            );
            const matchedCommodity = commodityOptions.find(
              (c: any) => c.commodity === item.productCommodity || c.commodityID === item.productCommodityID
            );
            const countries = (Array.isArray(item.countries) ? item.countries : [])
              .map((c: any) => ({
                countryID: c?.countryID ?? null,
                longName: c?.longName ?? null,
              }))
              .filter((co: any, i: number, arr: any[]) => {
                const k = co.countryID ?? co.longName;
                return k ? arr.findIndex((x: any) => (x.countryID ?? x.longName) === k) === i : false;
              });
            return {
              ...item,
              product: matchedProduct || item.product,
              productID: matchedProduct?.portfolioID || item.productID || null,
              productCommodity: matchedCommodity || item.productCommodity,
              productCommodityID: matchedCommodity?.commodityID || item.productCommodityID || null,
              commodityID: matchedCommodity?.commodityID || null,
              leadTimeMonths: parseLeadTimeValue(item.leadTimeMonths ?? item.LeadTimeMonths),
              leadTimeWeeks: parseLeadTimeValue(item.leadTimeWeeks ?? item.LeadTimeWeeks),
              countries,
            };
          })
          : []
      );
      setPaymentTerms(supplierData.terms?.paymentTerms || null);
      setMpaLink(supplierData.mpa?.link || "");
      setQualificationStatus(supplierData.qualificationStatus || {});
      setSelectedFTerm(supplierData.terms?.fTermID ? fTermsOptions.find(ft => ft.f_TermsID === supplierData.terms.fTermID) : null);
      setSelectedStatus(supplierData.mpa?.statusID ? statusOptions.find(st => st.supplierStatusID === supplierData.mpa.statusID) : null);
      setSelectedRegions(supplierData.supplierClassificationRegions?.length > 0 ? supplierData.supplierClassificationRegions.map((r: any) => r.regionID ? r : r.region) : []);
      setSelectedIntegratedOfferings(supplierData.integratedOfferings?.length > 0 ? supplierData.integratedOfferings : []);
      setContacts(supplierData.contacts?.length > 0 ? supplierData.contacts : [{ contactID: 0, name: "", surname: "", email: "", jobRole: "", commodity: [], selected: false, regions: [], newRegion: "" }]);
      setCommodityManagerRows(supplierData.commodityManagers?.length > 0 ? supplierData.commodityManagers : [{ cm_id: 0, manager: null, global: false }]);
      //setContractTypes(supplierData.contractTypes?.length > 0 ? supplierData.contractTypes :{ masterPurchasingAgreementMPA: false, nonDisclosureAgreement: false, project: false,} );
      setSelectedProductCommodity(null);
      setSelectedProducts([]);
      setExpandedHeader(null);

      if (supplierData.name?.length > 0) {
      }
      console.log("Supplier data:", supplierData);
      setEditMode(false);
      setShowDetailsScreen(true);
      setShowUpdateButton(false);
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message ?? error?.response?.data?.Message;
      const message = apiMessage
        ? `Error fetching supplier: ${apiMessage}`
        : error?.code === 'ECONNABORTED'
          ? 'Loading supplier timed out. Please try again.'
          : `Error fetching supplier: ${error?.message ?? error}`;
      setSnackbar({ open: true, message, severity: 'error' });
      console.error("Error fetching supplier:", error);
      setExpandedHeader(null);
    } finally {
      setLoading(false);
    }
  };


  const createEmptyContact = () => ({
    contactID: 0,
    name: "",
    surname: "",
    email: "",
    jobRole: "",
    commodity: [],
    selected: false,
    regions: [],
    newRegion: "",
  });

  const createEmptyCommodityManagerRow = () => ({
    cm_id: 0,
    manager: null,
    global: false,
  });

  const ClearSupplierObject = () => {

    // Identifiers & basic fields
    setsupplierId(0);
    setSupplierName("");
    setSupplierFolderLink("");
    setLogoUrl("");
    setportfolioDeatils("");

    setContractTypes({ masterPurchasingAgreementMPA: false, nonDisclosureAgreement: false, project: false });
    setFinancialRows([]);
    setFinancialFilterResetKey((key) => key + 1);
    setSelectedParseSuppliers([]);
    setParseSupplierInputValue("");
    setParseSupplierOptions([]);
    parseSupplierListSearchRef.current = '';
    setParseSupplierOpen(false);
    setPendingParseSuppliers(null);
    setParseConfirmOpen(false);
    setSelectedCommodities([]);
    setSelectedCommodity(null);
    setCommodityInput("");
    setSelectedPreferenceStatus(null);
    setProductPortfolio([]);
    setSelectedProducts([]);
    setSelectedCountries([]);
    setSelectedLeadTimeMonths('');
    setSelectedLeadTimeWeeks('');
    setSelectedProductCommodity(null);
    setPortfolioAddDialogOpen(false);
    setNewProduct("");

    // Terms & MPA
    setPaymentTerms(0);
    setSelectedFTerm(null);
    setMpaLink("");
    setSelectedStatus(null);
    // setNewFTerm("");
    // setNewStatus("");


    // Classification & offerings
    setQualificationStatus({
      qualificationStatus_NotQualified: false, qualificationStatus_ReadyforBusiness_R4B: false, qualificationStatus_ReadytoOrder_R4O: false,
    });
    setSelectedRegions([]);
    setSelectedIntegratedOfferings([]);

    // Contacts & managers
    setContacts([createEmptyContact()]);
    setCommodityManagerRows([createEmptyCommodityManagerRow()]);
    setCommodityInput("");
    // setNewCountry("");

    // Dependent selection
    setSelectedProductCommodity(null);

    // UI state (baseline after clear; override in caller as needed)
    setShowUpdateButton(false);
    // Leave edit/show flags for the caller to decide (done in the else above)
  };


  // Commodity Manager Email Modal state
  const getCommodityManagerLabel = (value: any): string => {
    if (typeof value === 'string') {
      const match = commodityManagerEmails.find((item) => getCommodityManagerMail(item) === value);
      return match ? getCommodityManagerDisplayName(match) : value;
    }
    return getCommodityManagerDisplayName(value);
  };

  const hasCommodityManagerMail = (mail: string): boolean => {
    const target = mail.trim().toLowerCase();
    if (!target) return false;
    return commodityManagerEmails.some((item) => getCommodityManagerMail(item).toLowerCase() === target);
  };

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [commodityManagerLoading, setCommodityManagerLoading] = useState(false);
  const [commodityManagerEmails, setCommodityManagerEmails] = useState<CommodityManagerOption[]>([]);
  const [newManagerEmail, setNewManagerEmail] = useState("");
  const [newManagerIsAdmin, setNewManagerIsAdmin] = useState(false);
  const [adminUpdateInProgress, setAdminUpdateInProgress] = useState<string | null>(null);

  const currentUserEmail = String(
    account?.username ??
    account?.idTokenClaims?.preferred_username ??
    account?.idTokenClaims?.email ??
    ''
  ).trim().toLowerCase();

  const canManageCommodityManagers = Boolean(currentUserEmail) && commodityManagerEmails.some(
    (item) => getCommodityManagerMail(item).toLowerCase() === currentUserEmail && getCommodityManagerIsSuperUser(item)
  );

  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<any[]>([]);
  const [selectedLeadTimeMonths, setSelectedLeadTimeMonths] = useState('');
  const [selectedLeadTimeWeeks, setSelectedLeadTimeWeeks] = useState('');
  const [portfolioAddDialogOpen, setPortfolioAddDialogOpen] = useState(false);
  const [portfolioEditDialogOpen, setPortfolioEditDialogOpen] = useState(false);
  const [portfolioEditIndex, setPortfolioEditIndex] = useState<number | null>(null);
  const [editPortfolioProduct, setEditPortfolioProduct] = useState<any>(null);
  const [editPortfolioCommodity, setEditPortfolioCommodity] = useState<any>(null);
  const [editPortfolioCountries, setEditPortfolioCountries] = useState<any[]>([]);
  const [editLeadTimeMonths, setEditLeadTimeMonths] = useState('');
  const [editLeadTimeWeeks, setEditLeadTimeWeeks] = useState('');
  // const [newCountry, setNewCountry] = useState("");
  const [newProduct, setNewProduct] = useState("");

  const loadCommodityManagers = async () => {
    setCommodityManagerLoading(true);
    try {
      const managers = await fetchCommodityManagers();
      const normalizedManagers = (Array.isArray(managers) ? managers : [])
        .map((item: any) => normalizeCommodityManager(item))
        .filter((item): item is CommodityManagerOption => Boolean(item));
      setCommodityManagerEmails(normalizedManagers);
    } catch (error) {
      console.error('Error loading commodity managers:', error);
      setSnackbar({ open: true, message: 'Error loading Commodity Managers', severity: 'error' });
    } finally {
      setCommodityManagerLoading(false);
    }
  };

  const handleOpenCommodityManagerModal = async () => {
    setNewManagerEmail("");
    setNewManagerIsAdmin(false);
    setEmailModalOpen(true);
    await loadCommodityManagers();
  };


  // Add new product to options via API
  const handleAddProductOption = async () => {
    const trimmed = newProduct.trim();
    if (!trimmed || productOptions.some((p) => p.portfolio === trimmed)) return;
    setLoading(true);
    try {
      const newObj = await addProduct(trimmed);
      console.log(newObj);
      if (newObj && newObj.portfolio) {
        console.log("New product added:", newObj);
        console.log("New product added:", newObj);
        //setProductPortfolio([...productPortfolio, { product: newObj, countries: [], productCommodity: null }]);
        setProductOptions([...productOptions, newObj]);
        setSelectedProducts((prev) =>
          prev.some((product) => isSameProductOption(product, newObj)) ? prev : [...prev, newObj]
        );
        setNewProduct("");
        setLoading(false);
      }
    } catch (err) {
      // fallback: add locally if API fails
      const newObj = { portfolioID: 0, portfolio: trimmed };
      setProductOptions([...productOptions, newObj]);
      setSelectedProducts((prev) =>
        prev.some((product) => getProductOptionLabel(product).toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, newObj]
      );
      setNewProduct("");
      setLoading(false);
    }
  };


  // Add new country to options

  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [productPortfolio, setProductPortfolio] = useState<{
    product: any;
    countries: any[];
    productCommodity?: any;
    leadTimeMonths?: number | null;
    leadTimeWeeks?: number | null;
  }[]>([]);

  const canAddNewProductOption =
    newProduct.trim().length > 0 &&
    !productOptions.some(
      (product) => String(product.portfolio ?? '').trim().toLowerCase() === newProduct.trim().toLowerCase()
    ) &&
    !selectedProducts.some(
      (product) => getProductOptionLabel(product).toLowerCase() === newProduct.trim().toLowerCase()
    );

  const getPortfolioCommodityId = (commodity: any) =>
    isCommodityObject(commodity) ? commodity.commodityID ?? null : null;

  const resetPortfolioAddForm = () => {
    setSelectedProductCommodity(null);
    setSelectedProducts([]);
    setSelectedCountries([]);
    setSelectedLeadTimeMonths('');
    setSelectedLeadTimeWeeks('');
    setNewProduct('');
  };

  const handleOpenPortfolioAddDialog = () => {
    if (selectedCommodities.length === 0) {
      setSnackbar({
        open: true,
        message: 'Add at least one commodity in Commodity Details before adding products.',
        severity: 'warning',
      });
      return;
    }
    resetPortfolioAddForm();
    setPortfolioAddDialogOpen(true);
  };

  const handleClosePortfolioAddDialog = () => {
    setPortfolioAddDialogOpen(false);
    resetPortfolioAddForm();
  };

  // Add products with countries for the selected commodity
  const handleAddProductPortfolio = () => {
    if (!selectedProductCommodity) {
      setSnackbar({
        open: true,
        message: 'Please select a commodity.',
        severity: 'error',
      });
      return;
    }

    if (selectedProducts.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one product.',
        severity: 'error',
      });
      return;
    }

    const commodityId = getPortfolioCommodityId(selectedProductCommodity);
    const newEntries: {
      product: any;
      countries: any[];
      productCommodity: any;
      leadTimeMonths: number | null;
      leadTimeWeeks: number | null;
    }[] = [];
    let skipped = 0;

    selectedProducts.forEach((product) => {
      const productId = getProductOptionId(product);
      const isDuplicate = productPortfolio.some(
        (entry) =>
          getProductOptionId(entry.product) === productId &&
          getPortfolioCommodityId(entry.productCommodity) === commodityId
      );

      if (isDuplicate) {
        skipped += 1;
        return;
      }

      newEntries.push({
        product,
        countries: selectedCountries,
        productCommodity: selectedProductCommodity,
        leadTimeMonths: parseLeadTimeValue(selectedLeadTimeMonths),
        leadTimeWeeks: parseLeadTimeValue(selectedLeadTimeWeeks),
      });
    });

    if (newEntries.length === 0) {
      setSnackbar({
        open: true,
        message: 'All selected products are already added for this commodity.',
        severity: 'warning',
      });
      return;
    }

    setProductPortfolio([...productPortfolio, ...newEntries]);
    setPortfolioAddDialogOpen(false);
    resetPortfolioAddForm();

    setSnackbar({
      open: true,
      message:
        skipped > 0
          ? `Added ${newEntries.length} product(s). ${skipped} duplicate(s) skipped.`
          : `Added ${newEntries.length} product(s) successfully!`,
      severity: 'success',
    });
  };

  // Remove product from portfolio
  const handleRemoveProductPortfolio = (index: number) => {
    setProductPortfolio(productPortfolio.filter((_, i) => i !== index));
  };

  const resetPortfolioEditForm = () => {
    setPortfolioEditIndex(null);
    setEditPortfolioProduct(null);
    setEditPortfolioCommodity(null);
    setEditPortfolioCountries([]);
    setEditLeadTimeMonths('');
    setEditLeadTimeWeeks('');
  };

  const handleOpenPortfolioEditDialog = (index: number) => {
    const entry = productPortfolio[index];
    if (!entry) return;
    setPortfolioEditIndex(index);
    setEditPortfolioProduct(entry.product ?? null);
    setEditPortfolioCommodity(entry.productCommodity ?? null);
    setEditPortfolioCountries(Array.isArray(entry.countries) ? [...entry.countries] : []);
    setEditLeadTimeMonths(formatLeadTimeValue(entry.leadTimeMonths));
    setEditLeadTimeWeeks(formatLeadTimeValue(entry.leadTimeWeeks));
    setPortfolioEditDialogOpen(true);
  };

  const handleClosePortfolioEditDialog = () => {
    setPortfolioEditDialogOpen(false);
    resetPortfolioEditForm();
  };

  const handleSavePortfolioEdit = () => {
    if (portfolioEditIndex == null) return;

    if (!editPortfolioCommodity) {
      setSnackbar({ open: true, message: 'Please select a commodity.', severity: 'error' });
      return;
    }
    if (!editPortfolioProduct) {
      setSnackbar({ open: true, message: 'Please select a product.', severity: 'error' });
      return;
    }

    const productId = getProductOptionId(editPortfolioProduct);
    const commodityId = getPortfolioCommodityId(editPortfolioCommodity);
    const isDuplicate = productPortfolio.some(
      (entry, i) =>
        i !== portfolioEditIndex &&
        getProductOptionId(entry.product) === productId &&
        getPortfolioCommodityId(entry.productCommodity) === commodityId
    );
    if (isDuplicate) {
      setSnackbar({
        open: true,
        message: 'That product is already added for this commodity.',
        severity: 'warning',
      });
      return;
    }

    setProductPortfolio((prev) =>
      prev.map((entry, i) =>
        i === portfolioEditIndex
          ? {
              ...entry,
              product: editPortfolioProduct,
              productCommodity: editPortfolioCommodity,
              countries: editPortfolioCountries,
              leadTimeMonths: parseLeadTimeValue(editLeadTimeMonths),
              leadTimeWeeks: parseLeadTimeValue(editLeadTimeWeeks),
            }
          : entry
      )
    );
    setPortfolioEditDialogOpen(false);
    resetPortfolioEditForm();
    setSnackbar({ open: true, message: 'Product portfolio row updated.', severity: 'success' });
  };
  // Supplier Details state
  // Supplier Details state
  // Supplier Details state

  React.useEffect(() => {
    // Only fetch suppliers after authentication is ready and access is granted
    if (!isAuthenticated || !account || accessCheckLoading || accessDenied) {
      return;
    }

    let isMounted = true;
    let hasFetched = false;

    const getAllSuppliers = async () => {
      // Double-check authentication and axios initialization before making API call
      if (!isAuthenticated || !account || accessDenied) {
        console.warn('Skipping getAllSuppliers - authentication not ready');
        return;
      }

      // Check if axios is initialized
      if (!isAxiosInitialized()) {
        console.warn('Skipping getAllSuppliers - axios not initialized yet');
        return;
      }

      if (hasFetched || Suppliers.length > 0) {
        return;
      }
      
      hasFetched = true;
      try {
        const suppliers = await fetchAllSuppliers();
        if (isMounted && Array.isArray(suppliers)) {
          setAllSuppliers(suppliers);
        }
      } catch (error: any) {
        console.error('Error fetching suppliers:', error);
        // Don't retry on 401 - it's an authentication issue that needs user action
        if (error?.response?.status !== 401) {
          hasFetched = false; // Allow retry on non-auth errors only
        }
      }
    };

    // Add small delay to ensure axios is initialized
    const timer = setTimeout(() => {
      getAllSuppliers();
    }, 200);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [Suppliers.length, isAuthenticated, account, accessCheckLoading, accessDenied]); // Include isAuthenticated and account in dependency array

  // Fetch regions, F-Terms, Supplier Statuses, and Integrated Offerings from API on mount
  React.useEffect(() => {
    // Only fetch data after authentication is ready and access is granted
    if (!isAuthenticated || !account || accessCheckLoading || accessDenied) {
      return;
    }

    let isMounted = true;
    let hasFetched = false;

    const getAllDropdowns = async () => {
      // Prevent multiple simultaneous calls
      if (hasFetched) {
        return;
      }

      // Double-check authentication before making API calls
      if (!isAuthenticated || !account) {
        console.warn('Skipping getAllDropdowns - authentication not ready');
        return;
      }

      // Wait for axios to be initialized (with timeout)
      let retries = 0;
      while (!isAxiosInitialized() && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!isAxiosInitialized()) {
        console.error('Axios not initialized after waiting. Skipping API calls.');
        return;
      }
      
      hasFetched = true;
      try {
        const [regions, fterms, statuses, offerings, countries, products, commodities, managers, preferenceStatuses] = await Promise.all([
          fetchRegions(),
          fetchFTerms(),
          fetchSupplierStatuses(),
          fetchIntegratedOfferings(),
          fetchProductionLocations(),
          fetchProducts(),
          fetchCommodities(),
          fetchCommodityManagers(),
          fetchPreferenceStatuses(),
        ]);

        if (isMounted) {
          if (Array.isArray(regions) && regions.length > 0) {
            const seen = new Set<string>();
            const deduped = regions.filter((r: any) => {
              const k = String(r?.regionID ?? r?.region ?? '');
              if (!k || seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            setRegionOptions(deduped);
            setContactRegionOptions(deduped);
          }
          if (Array.isArray(fterms) && fterms.length > 0) {
            const seen = new Set<string>();
            const deduped = fterms.filter((f: any) => {
              const k = String(f?.id ?? f?.name ?? '');
              if (!k || seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            setFTermsOptions(deduped);
          }
          if (Array.isArray(statuses) && statuses.length > 0) {
            const seen = new Set<string>();
            const deduped = statuses.filter((s: any) => {
              const k = String(s?.id ?? s?.name ?? '');
              if (!k || seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            setStatusOptions(deduped);
          }
          if (Array.isArray(offerings) && offerings.length > 0) {
            const seen = new Set<string>();
            const deduped = offerings.filter((o: any) => {
              const k = String(o?.id ?? o?.name ?? '');
              if (!k || seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            setIntegratedOfferingsOptions(deduped);
          }
          if (Array.isArray(countries) && countries.length > 0) {
            const seen = new Set<string>();
            const deduped = countries.filter((c: any) => {
              const k = String(c?.countryID ?? c?.longName ?? '');
              if (!k || seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            setCountryOptions(deduped);
          }
          if (Array.isArray(products) && products.length > 0) {
            setProductOptions(products);
          }
          if (Array.isArray(commodities) && commodities.length > 0) {
            setAllCommodities(commodities);
            setCommodityOptions(commodities);
          }
          if (Array.isArray(preferenceStatuses) && preferenceStatuses.length > 0) {
            setPreferenceStatusOptions(preferenceStatuses);
          }
          if (Array.isArray(managers) && managers.length > 0) {
            const normalizedManagers = managers
              .map((item: any) => normalizeCommodityManager(item))
              .filter((item): item is CommodityManagerOption => Boolean(item));
            setCommodityManagerEmails(normalizedManagers);
          }
        }
      } catch (error: any) {
        console.error('Error fetching dropdowns:', error);
        // Don't reset hasFetched on 401 - it's an authentication issue that needs backend fix
        // Only reset on network errors or other transient issues
        if (error?.response?.status === 401) {
          console.warn('Authentication failed (401). Backend may need to be redeployed with latest configuration.');
          // Keep hasFetched = true to prevent continuous retries
        } else if (error?.response?.status && error.response.status >= 500) {
          // Server errors might be transient, allow retry
          hasFetched = false;
        }
      }
    };
    
    // Add small delay to ensure axios is initialized
    const timer = setTimeout(() => {
      getAllDropdowns();
    }, 200);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [isAuthenticated, account, accessCheckLoading, accessDenied]);
  // const [newRegion, setNewRegion] = useState("");
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<any | null>(null);
  // const [newStatus, setNewStatus] = useState("");
  // Integrated Offerings state
  const [integratedOfferingsOptions, setIntegratedOfferingsOptions] = useState<any[]>([]);
  const [selectedIntegratedOfferings, setSelectedIntegratedOfferings] = useState<any[]>([]);
  // const [newIntegratedOffering, setNewIntegratedOffering] = useState("");
  // Commodity state
  const [commodityOptions, setCommodityOptions] = useState<any[]>([]);
  const [commodityInput, setCommodityInput] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState<any | null>(null);
  const [preferenceStatusOptions, setPreferenceStatusOptions] = useState<PreferenceStatus[]>([]);
  const [selectedPreferenceStatus, setSelectedPreferenceStatus] = useState<PreferenceStatus | null>(null);
  // const [selectedCommodityManager, setSelectedCommodityManager] = useState(true);

  const [selectedCommodities, setSelectedCommodities] = useState<any[]>([]);
  // const [ButtonClicked, setButtonClicked] = useState(false);

  const createCommodityOption = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return null;

    const existing = commodityOptions.find(
      (option) => option.commodity?.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) return existing;

    try {
      const newObj = await addCommodity(trimmed);
      if (newObj?.commodity) {
        setCommodityOptions((prev) => [...prev, newObj]);
        return newObj;
      }
    } catch {
      const fallback = { commodityID: 0, commodity: trimmed };
      setCommodityOptions((prev) => [...prev, fallback]);
      return fallback;
    }

    return null;
  }, [commodityOptions]);

   const handleOnChangeCommodity = async (commodityID: number) => {
    try {
      setLoading(true);
      // Clear supplier selection when commodity changes
      setsupplierId(0);
      setSearchValue("");
      setSelectedFilterSupplier(null);
      
      if (commodityID === 0) {
        // Fetch all suppliers when no commodity filter is selected
        const suppliers = await fetchAllSuppliers();
        if (Array.isArray(suppliers) && suppliers.length > 0) {
          setAllSuppliers(suppliers);
        } else {
          setAllSuppliers([]);
        }
      } else {
        // Fetch filtered suppliers by commodity
        const [suppliers] = await Promise.all([fetchAllSuppliersbyCommodityId(commodityID)]);
        if (Array.isArray(suppliers) && suppliers.length > 0) {
          setAllSuppliers(suppliers);
        } else {
          setAllSuppliers([]);
        }
      }
      setLoading(false);
      }
     catch (err) {
      // fallback: add locally if API fails
      setLoading(false);
      setSelectedCommodity(0);
    }

  };

  // Add selected commodity to list
  const handleAddSelectedCommodity = () => {
    if (!selectedCommodity) {
      setSnackbar({ open: true, message: 'Please select a commodity.', severity: 'warning' });
      return;
    }
    if (
      selectedCommodities.some((c) =>
        isCommodityObject(c) && isCommodityObject(selectedCommodity)
          ? c.commodityID === selectedCommodity.commodityID
          : c === selectedCommodity
      )
    ) {
      return;
    }

    const preferenceStatusId = normalizePreferenceStatusId(selectedPreferenceStatus?.id);
    const commodityToAdd = isCommodityObject(selectedCommodity)
      ? {
          ...selectedCommodity,
          preferenceStatusID: preferenceStatusId,
          preferenceStatus: preferenceStatusId ? (selectedPreferenceStatus?.status ?? '') : '',
        }
      : {
          commodity: selectedCommodity,
          commodityID: null,
          preferenceStatusID: preferenceStatusId,
          preferenceStatus: preferenceStatusId ? (selectedPreferenceStatus?.status ?? '') : '',
        };

    setSelectedCommodities([...selectedCommodities, commodityToAdd]);
    setSelectedCommodity(null);
    setCommodityInput("");
    setSelectedPreferenceStatus(null);
  };

  const handleCommodityPreferenceChange = (item: CommodityObj | string | number, statusId: number) => {
    const normalizedStatusId = normalizePreferenceStatusId(statusId);
    const status = normalizedStatusId
      ? preferenceStatusOptions.find((s) => s.id === normalizedStatusId)
      : null;
    setSelectedCommodities((prev) =>
      prev.map((c) => {
        if (commodityKey(c) !== commodityKey(item)) return c;
        if (!isCommodityObject(c)) return c;
        return {
          ...c,
          preferenceStatusID: normalizedStatusId,
          preferenceStatus: status?.status ?? '',
        };
      })
    );
  };

  // Remove commodity from selected list
  // const handleRemoveSelectedCommodity = (value: any) => {
  //   setSelectedCommodities(selectedCommodities.filter((c) => isCommodityObject(c) && isCommodityObject(value) ? c.commodityID !== value.commodityID : c !== value));
  // };
  const [showDetailsScreen, setShowDetailsScreen] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const handleEnterEditMode = useCallback(() => {
    flushSync(() => {
      setEditMode(true);
      setShowUpdateButton(true);
    });
  }, []);
  const [expandedHeader, setExpandedHeader] = useState<string | null>(null);
  const [, setSearchValue] = useState("");
  // Contact Details state
  const [contacts, setContacts] = useState([
    { contactID: 0, name: "", surname: "", email: "", jobRole: "", commodity: [], selected: false, regions: [], newRegion: "" },
  ]);
  const [financialRows, setFinancialRows] = useState<any[]>([]);
  const [financialFilterResetKey, setFinancialFilterResetKey] = useState(0);
  const [parseSupplierOptions, setParseSupplierOptions] = useState<ParseDataSupplier[]>([]);
  const [selectedParseSuppliers, setSelectedParseSuppliers] = useState<ParseDataSupplier[]>([]);
  const [parseSupplierInputValue, setParseSupplierInputValue] = useState("");
  const [parseSupplierLoading, setParseSupplierLoading] = useState(false);
  const [parseFinancialLoading, setParseFinancialLoading] = useState(false);
  const [parseConfirmOpen, setParseConfirmOpen] = useState(false);
  const [pendingParseSuppliers, setPendingParseSuppliers] = useState<ParseDataSupplier[] | null>(null);
  const [parseSupplierOpen, setParseSupplierOpen] = useState(false);
  const [pendingParseImport, setPendingParseImport] = useState<{
    details: ParseDataFinancialDetail[];
    suppliers: ParseDataSupplier[];
  } | null>(null);
  const parseSupplierSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parseSupplierAbortRef = useRef<AbortController | null>(null);
  const parseSupplierListSearchRef = useRef('');

  const hasFinancialRowData = useCallback((rows: any[]) => {
    return rows.some((row) =>
      String(row?.fy ?? '').trim() !== '' ||
      String(row?.value ?? '').trim() !== '' ||
      String(row?.period ?? '').trim() !== ''
    );
  }, []);

  const loadParseSupplierOptions = useCallback(async (search?: string) => {
    const trimmed = (search ?? '').trim();
    if (trimmed.length < PARSE_DATA_MIN_SEARCH_LENGTH) {
      setParseSupplierOptions([]);
      parseSupplierListSearchRef.current = '';
      return;
    }

    parseSupplierAbortRef.current?.abort();
    const controller = new AbortController();
    parseSupplierAbortRef.current = controller;
    setParseSupplierLoading(true);
    try {
      const suppliers = await fetchParseDataSuppliers(trimmed, 50);
      if (!controller.signal.aborted) {
        setParseSupplierOptions(suppliers);
        parseSupplierListSearchRef.current = trimmed;
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error('Error loading Parse Data suppliers:', error);
        setSnackbar({ open: true, message: 'Failed to load Parse Data suppliers.', severity: 'error' });
      }
    } finally {
      if (!controller.signal.aborted) {
        setParseSupplierLoading(false);
      }
    }
  }, []);

  const commodityByNameLookup = useMemo(() => {
    const map = new Map<string, { commodityID: number | null; commodity: string }>();
    commodityOptions.forEach((option) => {
      const name = option.commodity?.trim().toLowerCase() ?? '';
      if (name && !map.has(name)) {
        map.set(name, {
          commodityID: option.commodityID ?? null,
          commodity: option.commodity ?? name,
        });
      }
    });
    return map;
  }, [commodityOptions]);

  const restoreParseSuppliersFromFinancialRows = useCallback(async (rows: Array<{ supplierIFA?: string | null }>) => {
    const ifas = Array.from(
      new Set(
        rows
          .map((row) => String(row?.supplierIFA ?? '').trim())
          .filter((ifa) => ifa.length > 0)
      )
    );

    if (ifas.length === 0) {
      setSelectedParseSuppliers([]);
      setParseSupplierOptions([]);
      setParseSupplierInputValue('');
      return;
    }

    const suppliersFromApi = await fetchParseDataSuppliersByIfas(ifas);
    const byIfa = new Map<string, ParseDataSupplier>();

    ifas.forEach((ifa) => {
      const found = suppliersFromApi.find(
        (supplier) => supplier.supplierIFA.toLowerCase() === ifa.toLowerCase()
      );
      byIfa.set(ifa.toLowerCase(), found ?? { supplierIFA: ifa, supplierName: ifa });
    });

    suppliersFromApi.forEach((supplier) => {
      byIfa.set(supplier.supplierIFA.toLowerCase(), supplier);
    });

    const resolved = Array.from(byIfa.values()).sort((a, b) =>
      a.supplierName.localeCompare(b.supplierName, undefined, { sensitivity: 'base' })
    );

    setSelectedParseSuppliers(resolved);
    setParseSupplierOptions(resolved);
    setParseSupplierInputValue('');
  }, []);

  const parseSupplierAutocompleteOptions = useMemo(() => {
    const byIfa = new Map<string, ParseDataSupplier>();
    [...selectedParseSuppliers, ...parseSupplierOptions].forEach((supplier) => {
      const ifa = supplier?.supplierIFA?.trim();
      if (ifa) {
        byIfa.set(ifa.toLowerCase(), supplier);
      }
    });
    return Array.from(byIfa.values());
  }, [selectedParseSuppliers, parseSupplierOptions]);

  const hasLinkedParseFinancialRows = useMemo(
    () => financialRows.some((row) => String(row?.supplierIFA ?? '').trim().length > 0),
    [financialRows]
  );

  const hasUnlinkedParseFinancialRows = useMemo(
    () => hasFinancialRowData(financialRows) && !hasLinkedParseFinancialRows,
    [financialRows, hasFinancialRowData, hasLinkedParseFinancialRows]
  );

  const financialCommodityOptions = useMemo(() => {
    const byKey = new Map<string, { commodityID: number | null; commodity: string }>();
    financialRows.forEach((row) => {
      const id = row?.commodityID != null && Number(row.commodityID) > 0 ? Number(row.commodityID) : null;
      const nameFromRow = String(row?.commodity ?? '').trim();
      const resolvedName =
        nameFromRow ||
        (id != null
          ? commodityOptions.find((option) => option.commodityID === id)?.commodity?.trim() ?? ''
          : '');
      if (!resolvedName && id == null) return;
      const key = id != null ? `id:${id}` : `name:${resolvedName.toLowerCase()}`;
      if (!byKey.has(key)) {
        byKey.set(key, { commodityID: id, commodity: resolvedName });
      }
    });
    return Array.from(byKey.values()).sort((a, b) =>
      a.commodity.localeCompare(b.commodity, undefined, { sensitivity: 'base' })
    );
  }, [financialRows, commodityOptions]);

  const applyParseDataFinancialRows = useCallback((details: ParseDataFinancialDetail[]) => {
    const mapped = details.map((detail: ParseDataFinancialDetail, index: number) => {
      const trimmedCommodity = detail.commodity?.trim() ?? '';
      const matched = trimmedCommodity
        ? commodityByNameLookup.get(trimmedCommodity.toLowerCase())
        : undefined;
      const commodityMatch = matched ?? { commodityID: null as number | null, commodity: trimmedCommodity };

      return {
        id: Date.now() + index,
        fy: detail.fy,
        period: detail.period,
        value: detail.value,
        pov: detail.pov,
        pvo: detail.pvo,
        commodityID: commodityMatch.commodityID,
        commodity: commodityMatch.commodity,
        supplierIFA: detail.supplierIFA ?? '',
      };
    });

    setFinancialRows(mapped);
    setFinancialFilterResetKey((key) => key + 1);
  }, [commodityByNameLookup]);

  useEffect(() => {
    if (!pendingParseImport) {
      return;
    }

    applyParseDataFinancialRows(pendingParseImport.details);
    setSelectedParseSuppliers(pendingParseImport.suppliers);
    setPendingParseImport(null);
  }, [Commodities, pendingParseImport, applyParseDataFinancialRows]);

  const importParseDataFinancialDetails = useCallback(async (suppliers: ParseDataSupplier[]) => {
     
    if (suppliers.length === 0) {
      setSelectedParseSuppliers([]);
      return;
    }

    setParseFinancialLoading(true);
    try {
      const ifas = suppliers.map((supplier) => supplier.supplierIFA);
      const details: ParseDataFinancialDetail[] = await fetchParseDataFinancialDetails(ifas);
      if (details.length === 0) {
        setSnackbar({
          open: true,
          message: 'No financial details found for the selected Parse Data supplier(s).',
          severity: 'info',
        });
        return;
      }
      const commodities = await fetchAllCommodities();
      if (Array.isArray(commodities) && commodities.length > 0) {
        // Defer parse processing until commodities state is applied on next render.
        setPendingParseImport({ details, suppliers });
        setAllCommodities(commodities);
      } else {
        applyParseDataFinancialRows(details);
        setSelectedParseSuppliers(suppliers);
      }
      setParseSupplierOptions((prev) => {
        const byIfa = new Map<string, ParseDataSupplier>();
        [...prev, ...suppliers].forEach((supplier) => {
          const ifa = supplier?.supplierIFA?.trim();
          if (ifa) {
            byIfa.set(ifa.toLowerCase(), supplier);
          }
        });
        return Array.from(byIfa.values());
      });
      setSnackbar({
        open: true,
        message: `Loaded ${details.length} financial row(s) from ${suppliers.length} Parse Data supplier(s).`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error loading Parse Data financial details:', error);
      setSnackbar({ open: true, message: 'Failed to load Parse Data financial details.', severity: 'error' });
    } finally {
      setParseFinancialLoading(false);
    }
  }, [applyParseDataFinancialRows]);

  const handleParseSuppliersChange = useCallback((suppliers: ParseDataSupplier[]) => {
    setSelectedParseSuppliers(suppliers);
    if (suppliers.length === 0) {
      setParseSupplierInputValue("");
    }
  }, []);

  const handleFetchParseData = useCallback(() => {
    if (selectedParseSuppliers.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one Parse Data supplier.', severity: 'info' });
      return;
    }

    if (hasFinancialRowData(financialRows)) {
      setPendingParseSuppliers(selectedParseSuppliers);
      setParseConfirmOpen(true);
      return;
    }

    void importParseDataFinancialDetails(selectedParseSuppliers);
  }, [selectedParseSuppliers, financialRows, hasFinancialRowData, importParseDataFinancialDetails]);

  const handleParseConfirmReplace = useCallback(async () => {
    setParseConfirmOpen(false);
    const suppliers = pendingParseSuppliers;
    setPendingParseSuppliers(null);
    if (suppliers) {
      await importParseDataFinancialDetails(suppliers);
    }
  }, [pendingParseSuppliers, importParseDataFinancialDetails]);

  const scheduleParseSupplierSearch = useCallback((search: string) => {
    const trimmed = search.trim();
    if (trimmed.length < PARSE_DATA_MIN_SEARCH_LENGTH) {
      if (parseSupplierSearchTimerRef.current) {
        clearTimeout(parseSupplierSearchTimerRef.current);
      }
      setParseSupplierOptions([]);
      setParseSupplierLoading(false);
      return;
    }
    if (parseSupplierSearchTimerRef.current) {
      clearTimeout(parseSupplierSearchTimerRef.current);
    }
    parseSupplierSearchTimerRef.current = setTimeout(() => {
      void loadParseSupplierOptions(trimmed);
    }, 400);
  }, [loadParseSupplierOptions]);

  // const isNewSupplier = editMode && !searchValue.trim();
  // const handleAddSupplier = () => {
  //   if (searchValue.trim() !== "") {
  //     setEditMode(true); // View mode for existing
  //     setShowDetailsScreen(true);
  //   }
  // };
  const handleContactChange = (
    index: number,
    field: string,
    value: string | boolean | string[]
  ) => {
    const updatedContacts = [...contacts];
    (updatedContacts[index] as any)[field] = value;
    setContacts(updatedContacts);
  };
  const handleAddContact = () => {
    setContacts([...contacts, { contactID: 0, name: "", surname: "", email: "", jobRole: "", commodity: [], selected: false, regions: [], newRegion: "" }]);
  };

  const handleDeleteContactRow = (index: number) => {
    setContacts((prevContacts) => prevContacts.filter((_, i) => i !== index));
  };
  //As this is not used anywhere commenting for now as causing build failure in pipeline
  // const handleRemoveContact = (index: number) => {
  //   const updated = contacts.filter((_, i) => i !== index);
  //   setContacts(updated);
  // };
  // Add new commodity manager email via API
  const handleAddCommodityManagerEmail = async (mail: any) => {
    if (!canManageCommodityManagers) {
      setSnackbar({ open: true, message: 'Only Admins can add commodity managers.', severity: 'warning' });
      return;
    }

    const trimmed = mail ? mail.trim() : newManagerEmail.trim();
    const makeAdmin = newManagerIsAdmin;

    if (!trimmed || hasCommodityManagerMail(trimmed)) return;

    try {
      setLoading(true);
      const result = await addCommodityManager(trimmed, makeAdmin);
      if (result) {
        const normalizedManager = normalizeCommodityManager(result);
        setCommodityManagerEmails([
          ...commodityManagerEmails,
          normalizedManager ?? { mail: trimmed, name: '', isSuperUser: makeAdmin }
        ]);
        setNewManagerEmail("");
        setNewManagerIsAdmin(false);
        setSnackbar({
          open: true,
          message: makeAdmin ? 'Admin added successfully.' : 'Commodity Manager added successfully.',
          severity: 'success'
        });
      } else if (result === false) {
        setSnackbar({ open: true, message: 'Commodity Manager already exists.', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Error adding Commodity Manager',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCommodityManagerAdmin = async (mail: string, isAdmin: boolean) => {
    if (!canManageCommodityManagers) {
      setSnackbar({ open: true, message: 'Only Admins can grant or revoke Admin access.', severity: 'warning' });
      return;
    }

    const target = mail.trim().toLowerCase();
    const current = commodityManagerEmails.find(
      (item) => getCommodityManagerMail(item).toLowerCase() === target
    );
    if (!current) return;

    const currentlyAdmin = getCommodityManagerIsSuperUser(current);
    if (currentlyAdmin === isAdmin) return;

    if (!isAdmin) {
      if (target === currentUserEmail) {
        setSnackbar({ open: true, message: 'You cannot remove your own Admin access.', severity: 'warning' });
        return;
      }
      const remainingAdmins = commodityManagerEmails.filter(
        (item) => getCommodityManagerIsSuperUser(item) && getCommodityManagerMail(item).toLowerCase() !== target
      ).length;
      if (remainingAdmins === 0) {
        setSnackbar({ open: true, message: 'Cannot remove Admin from the last remaining Admin.', severity: 'warning' });
        return;
      }
    }

    try {
      setAdminUpdateInProgress(target);
      const result = await updateCommodityManagerAdmin(mail, isAdmin);
      const normalized = normalizeCommodityManager(result);
      setCommodityManagerEmails((prev) =>
        prev.map((item) =>
          getCommodityManagerMail(item).toLowerCase() === target
            ? (normalized ?? { ...item, isSuperUser: isAdmin })
            : item
        )
      );
      setSnackbar({
        open: true,
        message: isAdmin ? 'Admin access granted.' : 'Admin access removed.',
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Error updating Admin access',
        severity: 'error'
      });
    } finally {
      setAdminUpdateInProgress(null);
    }
  };

  const handleDeleteCommodityManagerEmail = async (mail: string) => {
    if (!canManageCommodityManagers) {
      setSnackbar({ open: true, message: 'Only Admins can delete commodity managers.', severity: 'warning' });
      return;
    }

    if (commodityManagerEmails.some(
      (item) => getCommodityManagerMail(item).toLowerCase() === mail.trim().toLowerCase()
        && getCommodityManagerIsSuperUser(item)
    )) {
      setSnackbar({ open: true, message: 'Demote Admin first before deleting.', severity: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const result = await deleteCommodityManager(mail);
      if (result) {
        setCommodityManagerEmails(commodityManagerEmails.filter(e => getCommodityManagerMail(e) !== mail));
        setSnackbar({ open: true, message: 'Commodity Manager deleted successfully.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Error deleting Commodity Manager. Please try again.', severity: 'error' });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Error deleting Commodity Manager',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  type CommodityManagerRow = {
    cm_id: number;
    manager: string | null;
    global: boolean;
    [key: string]: any;
  };
  const [commodityManagerRows, setCommodityManagerRows] = useState<CommodityManagerRow[]>([
    { cm_id: 0, manager: null, global: false }
  ]);



  const handleCommodityManagerRowChange = (index: number, field: string, value: any) => {
    console.log("Here is value", value);
    const updatedRows = [...commodityManagerRows];
    updatedRows[index][field] = value;
    if (field === "EAD" && value === true) {
      (updatedRows[index] as any)["global"] = false;
    }
    if (field === "EAD" && value === false) {
      (updatedRows[index] as any)["global"] = true;
    }
    setCommodityManagerRows(updatedRows);
  };
  const handleAddCommodityManagerRow = () => {
    setCommodityManagerRows([...commodityManagerRows, { cm_id: 0, manager: null, global: false }]);
  };
  const handleDeleteCommodityManagerRow = (index: number) => {
    setCommodityManagerRows(commodityManagerRows.filter((_, i) => i !== index));
  };

  // Migration lockout — banner only, no login / app access
  if (isMigrationModeEnabled()) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1533 0%, #2d1b4e 50%, #1a1533 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(138, 0, 229, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 4,
              p: { xs: 3, sm: 5 },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/SE_Logo_Color_RGB.png`}
                alt="Siemens Energy Logo"
                style={{ height: 56, width: 'auto', objectFit: 'contain', display: 'block' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = SELogo;
                }}
              />
            </Box>
            <Typography
              sx={{
                color: 'rgb(26, 21, 51)',
                fontWeight: 700,
                fontSize: '24px',
                textAlign: 'center',
                letterSpacing: '0.3px',
              }}
            >
              Procurement Supplier Management
            </Typography>
            <Box sx={{ width: '100%' }}>
              <MigrationBanner />
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  // Access denied — shown after auto sign-out (state restored from sessionStorage)
  if (accessDenied) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1533 0%, #2d1b4e 50%, #1a1533 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 4,
              p: 5,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1533' }}>
              Access Denied
            </Typography>
            <Typography variant="body1" sx={{ color: '#4b5563', maxWidth: 420 }}>
              {accessDeniedMessage}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              Ask a Commodity Manager Admin to add your email, then click Try again.
            </Typography>
            <PrimaryButton
              onClick={() => { void handleTryAccessAgain(); }}
              sx={{ mt: 1, px: 3 }}
            >
              Try again
            </PrimaryButton>
          </Box>
        </Container>
      </Box>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1533 0%, #2d1b4e 50%, #1a1533 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(138, 0, 229, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 4,
              p: 6,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/SE_Logo_Color_RGB.png`}
                  alt="Siemens Energy Logo"
                  style={{ 
                    height: 60, 
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                  onError={(e) => {
                    // Fallback to white logo if color logo fails
                    (e.target as HTMLImageElement).src = SELogo;
                  }}
                />
              </Box>
              <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
                <Typography 
                  sx={{ 
                    color: 'rgb(26, 21, 51)', 
                    fontWeight: 700,
                    fontSize: '26px',
                    lineHeight: 1.4,
                    mb: 2,
                    letterSpacing: '0.3px',
                  }}
                >
                  Procurement Supplier Management
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#666', 
                    textAlign: 'center',
                    fontSize: '15px',
                    lineHeight: 1.6,
                    maxWidth: 400,
                    mx: 'auto',
                    fontWeight: 400,
                    mb: 1,
                  }}
                >
                  Sign in with your Microsoft account to continue
                </Typography>
              </Box>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
              <PrimaryButton
                size="large"
                fullWidth
                startIcon={<LoginIcon />}
                onClick={async () => {
                  try {
                    console.log('Login button clicked');
                    await login();
                    console.log('Login successful');
                  } catch (error: any) {
                    console.error('Login error:', error);
                    let errorMessage = 'Login failed. Please try again.';
                    
                    // Check for specific Azure AD errors
                    if (error?.errorCode === 'invalid_client' || 
                        error?.message?.includes('AADSTS650057') ||
                        error?.errorMessage?.includes('AADSTS650057')) {
                      errorMessage = 'Authentication configuration issue. Please contact your administrator. Error: AADSTS650057';
                    } else if (error?.errorCode === 'interaction_required') {
                      errorMessage = 'Please complete the authentication process in the popup window.';
                    } else if (error?.message) {
                      errorMessage = `Login failed: ${error.message}`;
                    }
                    
                    setSnackbar({
                      open: true,
                      message: errorMessage,
                      severity: 'error'
                    });
                  }
                }}
                sx={{
                  fontSize: '16px',
                  py: 1.75,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(138, 0, 229, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(138, 0, 229, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Sign In with Microsoft
              </PrimaryButton>
            </Box>

            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid #e0e0e0',
                width: '100%',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#999',
                  fontSize: '12px',
                }}
              >
                Secure authentication powered by Microsoft Azure AD
              </Typography>
            </Box>
          </Box>
        </Container>
        
        {/* Snackbar for error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // While verifying Commodity Manager membership
  if (accessCheckLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          background: 'linear-gradient(135deg, #1a1533 0%, #2d1b4e 50%, #1a1533 100%)',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#fff' }} />
        <Typography sx={{ color: '#fff', fontSize: 14 }}>
          Verifying access…
        </Typography>
      </Box>
    );
  }

  // 🔍 Main screen
  if (!showDetailsScreen) {
    return (
      <React.Fragment>
        {(saving || loading) && (
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CircularProgress size={70} thickness={5} color="secondary" />
          </Box>
        )}
        {/* Snackbar notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Container>
          {/* Sticky header with only the Commodity Managers button */}
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              top: 0,
              width: '98%',
              bgcolor: 'rgb(26, 21, 51)',
              boxShadow: 1,
              py: 2,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 1200,
            }}
          >
            {/* Logo on the left */}
            <Box display="flex" alignItems="center" ml={2}>
              <img
                src={SELogo}
                alt="SE Logo"
                style={{ height: 36, width: 'auto', marginRight: 12 }}
              />

              <Typography
                sx={{
                  color: 'var(--Text-default, #ffffff)',
                  fontFamily: 'var(--Font-family-text, "Siemens Sans")',
                  fontSize: 'var(--Font-size-text-large, 18px)',
                  fontWeight: 700,
                  lineHeight: 'var(--Font-line-height-text-large, 28px)',
                  letterSpacing: 'var(--Font-font-spacing-default, 0)',
                  marginLeft: 2
                }}
              >
                Procurement - Supplier Management
              </Typography>
            </Box>
            {/* Right side: User info and buttons */}
            <Box display="flex" alignItems="center" gap={2}>
              {/* Commodity Managers button */}
              <Button
                variant="outlined"
                startIcon={
                  commodityManagerLoading
                    ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                    : <GroupIcon sx={{ color: '#fff' }} />
                }
                onClick={handleOpenCommodityManagerModal}
                disabled={commodityManagerLoading}
                sx={{ borderRadius: 2, fontWeight: 500, textTransform: 'none', color: '#fff', borderColor: '#fff', '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.08)' } }}
              >
                Commodity Managers
              </Button>
              {account && (
                <>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={1.5}
                    onClick={handleUserMenuClick}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      px: 2,
                      py: 0.75,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      {(
                        account.name || 
                        account.username || 
                        account.idTokenClaims?.name ||
                        account.idTokenClaims?.preferred_username ||
                        account.idTokenClaims?.email ||
                        'U'
                      ).charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {account.name || 
                         account.idTokenClaims?.name ||
                         account.username || 
                         account.idTokenClaims?.preferred_username ||
                         account.idTokenClaims?.email ||
                         'User'}
                      </Typography>
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={userMenuOpen}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography>Sign Out</Typography>
                    </MenuItem>
                  </Menu>
                </>
              )}
          </Box>
          </Box>
          {/* Main content: Supplier Management label and search UI remain here */}
          <Box py={2} textAlign="center" mt={30}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Supplier Management
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Search existing suppliers or add a new one
            </Typography>
          </Box>
          <Box mt={4} display="flex" justifyContent="center">
            <Box width={600}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <FilterListIcon sx={{ color: 'rgb(138, 0, 229)', fontSize: '1.1rem' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                  Select a commodity to filter suppliers
                </Typography>
              </Box>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <Box sx={{ width: '42%', flexShrink: 0 }}>
                  <Autocomplete
                    options={Commodities}
                    getOptionLabel={(option) => option.commodity}
                    value={selectedFilterCommodity}
                    onChange={(event, newValue) => {
                      setSelectedFilterCommodity(newValue);
                      if (newValue) {
                        //(nov17)
                        setCommodityId(newValue.commodityID); // Pass selected commodity object
                        handleOnChangeCommodity(newValue.commodityID);
                      }

                      else {
                        setCommodityId(0); // Clear when X is clicked
                        handleOnChangeCommodity(0);
                      }

                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter by Commodity"
                        fullWidth
                        size="small"
                        helperText={commodityId > 0 ? `${Suppliers.length} supplier(s) found` : "Optional - filters supplier list"}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgb(138, 0, 229)',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.813rem',
                            '&.Mui-focused': {
                              color: 'rgb(138, 0, 229)',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: '0.65rem',
                            mt: 0.5,
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        minHeight: '36px',
                      },
                    }}
                  />
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mt: 2,
                  color: 'rgb(138, 0, 229)',
                  opacity: commodityId > 0 ? 1 : 0.3,
                  transition: 'opacity 0.2s',
                }}>
                  <ArrowForwardIcon sx={{ fontSize: '1.3rem' }} />
                </Box>
                <Box sx={{ width: '52%', flexShrink: 0 }}>
                  <Autocomplete
                    options={Suppliers}
                    getOptionLabel={getSupplierOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      getSupplierOptionId(option) > 0 &&
                      getSupplierOptionId(option) === getSupplierOptionId(value)
                    }
                    value={selectedFilterSupplier}
                    disabled={loading}
                    onChange={(event, newValue) => {
                      setSelectedFilterSupplier(newValue);
                      const id = getSupplierOptionId(newValue);
                      setsupplierId(id);
                      setSearchValue(id ? String(id) : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Supplier"
                        fullWidth
                        size="small"
                        helperText={commodityId > 0 ? "Filtered by selected commodity" : "All suppliers available"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgb(138, 0, 229)',
                                borderWidth: '2px',
                              },
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.813rem',
                            '&.Mui-focused': {
                              color: 'rgb(138, 0, 229)',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: '0.65rem',
                            mt: 0.5,
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        minHeight: '36px',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

          </Box>
          <Box mt={2.5} display="flex" justifyContent="center">
            <Box width={600}>
              <Box display="flex" gap={1.5} alignItems="flex-start">
                <Box sx={{ width: '42%', flexShrink: 0 }}>
                  <SecondaryButton
                    size="small"
                    onClick={() => handleExistingSupplier(getSupplierOptionId(selectedFilterSupplier))}
                    disabled={!selectedFilterSupplier || getSupplierOptionId(selectedFilterSupplier) <= 0 || isDeleted}
                    fullWidth
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.6,
                      px: 1.5,
                      minHeight: '32px',
                    }}
                  >
                    View Supplier
                  </SecondaryButton>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: '1.3rem',
                }} />
                <Box sx={{ width: '52%', flexShrink: 0 }}>
                  <PrimaryButton
                    size="small"
                    onClick={() => {
                      setSearchValue("");
                      setEditMode(true);
                      ClearSupplierObject();
                      setShowDetailsScreen(true);
                      setShowUpdateButton(false);
                    }}
                    fullWidth
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.6,
                      px: 1.5,
                      minHeight: '32px',
                    }}
                  >
                    Add Supplier
                  </PrimaryButton>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Commodity Manager Email Modal */}
          <Dialog
            open={emailModalOpen}
            onClose={() => setEmailModalOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: 8,
                width: 'min(860px, 95vw)',
                minHeight: '70vh',
              }
            }}
          >
            <DialogTitle sx={{ bgcolor: '#f5f5f5', borderTopLeftRadius: 12, borderTopRightRadius: 12, py: 2, px: 3, fontWeight: 700, fontSize: 20, color: '#333', letterSpacing: 1 }}>
              Commodity Managers
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
              <Box display="flex" gap={1.5} mb={2} mt={1} alignItems="center">
                <TextField
                  label="Add Email"
                  value={newManagerEmail}
                  onChange={e => setNewManagerEmail(e.target.value)}
                  size="small"
                  fullWidth
                  type="email"
                  autoFocus
                  disabled={commodityManagerLoading || !canManageCommodityManagers}
                  sx={{ bgcolor: '#fff', borderRadius: 2 }}
                />
                <FormControlLabel
                  sx={{ m: 0, whiteSpace: 'nowrap' }}
                  control={
                    <Checkbox
                      checked={newManagerIsAdmin}
                      onChange={(e) => setNewManagerIsAdmin(e.target.checked)}
                      disabled={commodityManagerLoading || !canManageCommodityManagers}
                      sx={{
                        color: '#8A00E5',
                        '&.Mui-checked': { color: '#8A00E5' },
                      }}
                    />
                  }
                  label="Admin"
                />
                <PrimaryButton
                  size="small"
                  disabled={
                    commodityManagerLoading ||
                    !canManageCommodityManagers ||
                    !newManagerEmail.trim() ||
                    !/^\S+@\S+\.\S+$/.test(newManagerEmail) ||
                    hasCommodityManagerMail(newManagerEmail)
                  }
                  onClick={() => handleAddCommodityManagerEmail(null)}
                  sx={{ minWidth: 40, px: 2, mt: 0.2 }}
                >
                  Add
                </PrimaryButton>
              </Box>
              {!commodityManagerLoading && !canManageCommodityManagers && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Only Admins can add or delete commodity manager emails.
                </Typography>
              )}
              <List dense sx={{ bgcolor: '#f8f9fa', borderRadius: 2, boxShadow: 0, px: 1, py: 1, minHeight: 48 }}>
                {commodityManagerLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : commodityManagerEmails.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1, ml: 1 }}>
                    No emails added.
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', px: 1.5, pb: 0.8, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ flex: '0 0 32%', fontWeight: 700, color: '#555' }}>
                        Name
                      </Typography>
                      <Typography variant="caption" sx={{ flex: '0 0 42%', fontWeight: 700, color: '#555', textAlign: 'center' }}>
                        Email
                      </Typography>
                      <Typography variant="caption" sx={{ flex: '0 0 16%', fontWeight: 700, color: '#555', textAlign: 'center' }}>
                        Admin
                      </Typography>
                      <Typography variant="caption" sx={{ flex: '0 0 10%', fontWeight: 700, color: '#555', textAlign: 'center' }}>
                        
                      </Typography>
                    </Box>
                    {commodityManagerEmails.map((manager, cM_ID) => {
                      const managerMail = getCommodityManagerMail(manager);
                      const managerMailKey = managerMail.toLowerCase();
                      const isAdmin = getCommodityManagerIsSuperUser(manager);
                      const isSelf = managerMailKey === currentUserEmail;
                      const remainingAdmins = commodityManagerEmails.filter(
                        (item) => getCommodityManagerIsSuperUser(item) && getCommodityManagerMail(item).toLowerCase() !== managerMailKey
                      ).length;
                      const isLastAdmin = isAdmin && remainingAdmins === 0;
                      const adminBusy = adminUpdateInProgress === managerMailKey;
                      let adminDisabled = !canManageCommodityManagers || adminBusy;
                      let adminTitle = '';
                      if (!canManageCommodityManagers) {
                        adminTitle = 'Only Admins can grant or revoke Admin access.';
                      } else if (isAdmin && isSelf) {
                        adminDisabled = true;
                        adminTitle = 'You cannot remove your own Admin access.';
                      } else if (isLastAdmin) {
                        adminDisabled = true;
                        adminTitle = 'Cannot remove Admin from the last remaining Admin.';
                      }
                      const deleteDisabled = isAdmin;
                      const deleteTitle = isAdmin ? 'Demote Admin first before deleting.' : 'Delete email';

                      return (
                      <ListItem key={managerMail || cM_ID} divider sx={{ borderRadius: 2, bgcolor: '#fff', boxShadow: 0, mb: 0.5, px: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography
                            variant="body2"
                            title={getCommodityManagerName(manager) || '-'}
                            sx={{
                              flex: '0 0 32%',
                              minWidth: 0,
                              color: '#333',
                              fontWeight: 500,
                              pr: 1,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {getCommodityManagerName(manager) || '-'}
                          </Typography>
                          <Typography
                            variant="body2"
                            title={managerMail}
                            sx={{
                              flex: '0 0 42%',
                              minWidth: 0,
                              color: '#333',
                              pr: 1,
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {managerMail}
                          </Typography>
                          <Box sx={{ flex: '0 0 16%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 42 }}>
                            {adminBusy ? (
                              <CircularProgress size={20} sx={{ color: '#8A00E5' }} />
                            ) : (
                              <Tooltip title={adminTitle} disableHoverListener={!adminTitle}>
                                <span>
                                  <Checkbox
                                    checked={isAdmin}
                                    disabled={adminDisabled}
                                    onChange={(e) => handleToggleCommodityManagerAdmin(managerMail, e.target.checked)}
                                    inputProps={{ 'aria-label': 'Admin' }}
                                    sx={{
                                      color: '#8A00E5',
                                      '&.Mui-checked': { color: '#8A00E5' },
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                          <Box sx={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center', pr: 0.5 }}>
                            {canManageCommodityManagers ? (
                              <DeleteIconButton
                                label={deleteTitle}
                                disabled={deleteDisabled}
                                onClick={() => handleDeleteCommodityManagerEmail(managerMail)}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </ListItem>
                      );
                    })}
                  </>
                )}
              </List>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'flex-end' }}>
              <SecondaryButton onClick={() => setEmailModalOpen(false)} sx={{ px: 2 }}>
                Close
              </SecondaryButton>
            </DialogActions>
          </Dialog>
        </Container>

      </React.Fragment>

    );
  }
  // 🧾 Supplier Details screen
  return (
    <React.Fragment>
      {/* Loader overlay */}
      {(saving || loading) && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(255,255,255,0.7)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={70} thickness={5} color="secondary" />
        </Box>
      )}
      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Container sx={{ pb: 10 }}>

        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '98%',
            bgcolor: 'rgb(26, 21, 51)',
            boxShadow: 1,
            py: 2,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1200,
          }}
        >
          {/* Logo on the left */}
          <Box display="flex" alignItems="center" ml={2}>
            <img
              src={SELogo}
              alt="SE Logo"
              style={{ height: 36, width: 'auto', marginRight: 12 }}
            />

            <Typography
              sx={{
                color: 'var(--Text-default, #ffffff)',
                fontFamily: 'var(--Font-family-text, "Siemens Sans")',
                fontSize: 'var(--Font-size-text-large, 18px)',
                fontWeight: 700,
                lineHeight: 'var(--Font-line-height-text-large, 28px)',
                letterSpacing: 'var(--Font-font-spacing-default, 0)',
                marginLeft: 2
              }}
            >
              Supplier Details
            </Typography>

            <Button
              size="medium"
              onClick={() => {
                setShowDetailsScreen(false);
                setEditMode(true);
                setShowUpdateButton(false);
                ClearSupplierObject();
                setExpandedHeader(null);
              }}
              sx={{
                //nov17
                color: '#FFFFFF', // Text color white
                textAlign: 'center',
                fontSize: '18px',
                fontFamily: 'var(--Font-family-text, "Siemens Sans")',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'var(--Font-line-height-text-default, 28px)',
                letterSpacing: 'var(--Font-font-spacing-default, 0)',
                textTransform: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                backgroundColor: 'transparent', // No background
                boxShadow: 'none', // No shadow
                transition: 'all 0.3s ease',
                //left: 1150, // Align right,
                position: 'absolute',
                top: 8,
                right: 16,
                '&:hover': {
                  backgroundColor: 'transparent',
                  transform: 'scale(1.05)',
                },
                '& svg': {
                  marginRight: '8px',
                  fill: '#FFFFFF', // ✅ Icon color white
                  alignItems: 'center',
                },

              }}
              startIcon={
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2 10L12 2L22 10V22H14V16H10V22H2V10ZM16 20H20V11L12 4.6L4 11V20H8V14H16V20Z"
                    fill="#FFFFFF" // ✅ White fill for the icon
                  />
                </svg>
              }
            >

            </Button>

          </Box>
        </Box>


        <Typography variant="h6" mt={15} mb={1} sx={{ fontWeight: 600 }}>
          {editMode && !showUpdateButton ? "New Supplier" : editMode && showUpdateButton ? "Update Supplier" : "Supplier Details"}
        </Typography>
        <Box
          component="fieldset"
          sx={{ border: 0, m: 0, p: 0, minWidth: 0, display: 'contents' }}
        >
        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 3, background: '#f8f9fa' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Supplier Information</Typography>
          <Box mt={1} display="flex" gap={1}>
            <TextField
              label="Supplier Name*"
              type="text"
              value={name}
              onChange={(e) => {
                const newValue = e.target.value;

                // Always update state first
                setSupplierName(newValue);

                let error = '';


                if (newValue.trim() === '') {
                  error = ''; // No error for empty input
                } else if (newValue.length < 3) {
                  error = 'Supplier name must be at least 3 characters';
                } else if (newValue.length > 100) {
                  error = 'Supplier name cannot exceed 100 characters';
                } else if (/^[\s\W]/.test(newValue)) {
                  error = 'Name cannot start with a space or special character';
                } else if (!/[a-zA-Z0-9]/.test(newValue)) {
                  error = 'Name must contain at least one letter or number';
                }

                setErrorMessageSN(error);
              }}
              onKeyDown={(e) => {

                if (name.length === 0 && /^[\s\W]$/.test(e.key)) {
                  e.preventDefault();
                  setErrorMessageSN('Name cannot start with a space or special character');
                }
              }}
              onPaste={(e) => {
                const pasteText = e.clipboardData.getData('text');
                if (/^[\s\W]/.test(pasteText)) {
                  e.preventDefault();
                  setErrorMessageSN('Pasting text starting with space or special character is not allowed');
                } else if (pasteText.length > 100) {
                  e.preventDefault();
                  setErrorMessageSN('Supplier name cannot exceed 100 characters');
                }
              }}


              error={Boolean(errorMessage)}
              helperText={errorMessage || undefined}

              fullWidth
              size="small"
            />
            <TextField
              label="Supplier Folder Link (SharePoint)"
              value={folderLink}
              onChange={(e) => setSupplierFolderLink(e.target.value)}
              fullWidth
              size="small"
              title="Double-click to open link"
              InputProps={{
                style: {
                  color: isValidUrl(folderLink)
                    ? editMode
                      ? '#8A00E5' // Darker blue in edit mode
                      : '#bc6cf2ff' // Lighter blue in view mode
                    : 'inherit',
                  textDecoration: isValidUrl(folderLink) ? 'underline' : 'none',
                  cursor: isValidUrl(folderLink) ? 'pointer' : 'default'
                },
                onDoubleClick: () => {
                  if (isValidUrl(folderLink)) {
                    window.open(folderLink, '_blank');
                  }
                }
              }}
              inputProps={{
                style: {
                  cursor: isValidUrl(folderLink) ? 'pointer' : 'default'
                }
              }}
              error={folderLink.length > 0 && !isValidUrl(folderLink)}
              helperText={
                folderLink.length > 0 && !isValidUrl(folderLink)
                  ? 'Please enter a valid URL (e.g., https://example.com)'
                  : ''
              }
            />

            <TextField
              label="Logo (web-URL)"
              value={logo}
              onChange={(e) => setLogoUrl(e.target.value)}
              fullWidth
              size="small"
              title="Double-click to open link"
              InputProps={{
                style: {
                  color: isValidUrl(logo)
                    ? editMode
                      ? '#8A00E5' // Darker blue in edit mode
                      : '#bc6cf2ff' // Lighter blue in view mode
                    : 'inherit',
                  textDecoration: isValidUrl(logo) ? 'underline' : 'none',
                  cursor: isValidUrl(logo) ? 'pointer' : 'default'
                },
                onDoubleClick: () => {
                  if (isValidUrl(logo)) {
                    window.open(logo, '_blank');
                  }
                }
              }}
              inputProps={{
                style: {
                  cursor: isValidUrl(logo) ? 'pointer' : 'default' // ✅ Ensures pointer on hover
                }
              }}
              error={logo.length > 0 && !isValidUrl(logo)}
              helperText={
                logo.length > 0 && !isValidUrl(logo)
                  ? 'Please enter a valid URL (e.g., https://example.com/logo.png)'
                  : ''
              }
            />

          </Box>

        </Paper>
        {renderCollapsibleSection(
          "Commodity Managers",
          expandedHeader,
          setExpandedHeader,
          <Paper elevation={0} sx={{ p: 2, background: '#f9fafb', borderRadius: 3, boxShadow: 1 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              {commodityManagerRows.map((row, index) => (
                <Box key={row.id} display="flex" gap={2} alignItems="center">
                  {/* <Autocomplete
                  options={commodityManagerEmails}
                  getOptionLabel={option => option}
                  value={row.manager}
                  onChange={(_e, newValue) => handleCommodityManagerRowChange(index, 'manager', newValue)}
                  renderInput={params => (
                    <TextField {...params} label="Commodity Manager" size="small" sx={{ bgcolor: '#fff', borderRadius: 2, minWidth: 400 }} />
                  )}
                  sx={{ minWidth: 180 }}
                /> */}

                  <Autocomplete
                    options={commodityManagerEmails}
                    getOptionLabel={(option) => getCommodityManagerLabel(option)}
                    freeSolo
                    value={row.manager}
                    onChange={(_e, newValue) => {
                      if (!newValue) {
                        handleCommodityManagerRowChange(index, 'manager', '');
                        return;
                      }
                      const selectedMail = typeof newValue === 'string' ? newValue : getCommodityManagerMail(newValue);
                      handleCommodityManagerRowChange(index, 'manager', selectedMail || '');
                    }}
                    onInputChange={(_e, newValue) => {
                      const val = (typeof newValue === 'string' ? newValue : '').trim();
                      const validEmail = /^\S+@\S+\.\S+$/.test(val);
                      setValidationErrors(prev => ({
                        ...prev,
                        [index]: !validEmail && val !== ''
                      }));

                      // setSelectedCommodityManager(commodityManagerEmails.includes(val));

                      handleCommodityManagerRowChange(index, 'manager', val);

                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Commodity Manager"
                        size="small"
                        sx={{ bgcolor: '#fff', borderRadius: 2, minWidth: 400 }}
                      />
                    )}
                    sx={{ minWidth: 180 }}

                    // PopperComponent={(props) => (
                    //   <Popper {...props} placement="top-start" style={{ zIndex: 1300 }} />
                    // )}


                    disablePortal
                    openOnFocus
                    autoHighlight
                  />




                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!row.global}
                        onChange={e => handleCommodityManagerRowChange(index, 'EAD', e.target.checked)}
                      />
                    }
                    label="EAD"
                    sx={{ ml: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={row.global}
                        onChange={e => handleCommodityManagerRowChange(index, 'global', e.target.checked)}
                      />
                    }
                    label="Global"
                    sx={{ ml: 1 }}
                  />


                  <SecondaryButton
                    size="small"
                    title="Click to add new email address"
                    onClick={() => {
                      handleAddCommodityManagerEmail(row.manager || "");       // call on click
                      //setNewManagerEmail(row.manager || "");        // then update state
                    }}
                    disabled={validationErrors[index] || !row.manager || hasCommodityManagerMail(row.manager)}
                    sx={{ mt: 1, top: -3, maxWidth: 180 }}
                  >
                    Add
                  </SecondaryButton>

                  {(showUpdateButton || editMode) && (
                    <DeleteIconButton label="Remove manager" sx={{ left: -10 }} onClick={() => handleDeleteCommodityManagerRow(index)} />
                  )}


                </Box>
              ))}
              <Box sx={{
                display: 'flex',
                gap: 2, // space between items
                alignItems: 'center', // vertical alignment

                // bgcolor: '#f5f5f5', // background color
                // p: 2, // padding
                borderRadius: 2 // rounded corners
              }}>
                {(showUpdateButton || editMode) && (

                  <SecondaryButton
                    size="small"
                    onClick={handleAddCommodityManagerRow}
                    sx={{ mt: 1, maxWidth: 180 }}
                  >
                    Add Manager Row
                  </SecondaryButton>
                )}

                {/* {(showUpdateButton || editMode) && (
              <Button
                  variant="outlined"
                  startIcon={<PersonIcon sx={{}} />}
                  onClick={() => setEmailModalOpen(true)}
                  sx={{ mt: 1, maxWidth: 180 }}
                >
                  Commodity Managers
                </Button>
            )} */}

              </Box>
            </Box>
          </Paper>
        )}
        <Dialog open={parseConfirmOpen} onClose={() => { setParseConfirmOpen(false); setPendingParseSuppliers(null); }}>
          <DialogTitle>Replace financial details?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Importing Parse Data for{' '}
              <strong>
                {pendingParseSuppliers && pendingParseSuppliers.length > 0
                  ? `${pendingParseSuppliers.length} supplier(s)`
                  : 'the selected suppliers'}
              </strong>{' '}
              will replace the current financial rows. Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <SecondaryButton
              onClick={() => { setParseConfirmOpen(false); setPendingParseSuppliers(null); }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => { void handleParseConfirmReplace(); }}
              sx={{ px: 2.5 }}
            >
              Replace rows
            </PrimaryButton>
          </DialogActions>
        </Dialog>
        {renderCollapsibleSection(
          "Financial Details",
          expandedHeader,
          setExpandedHeader,
          <Paper elevation={0} sx={{ p: 2, background: '#f9fafb', borderRadius: 2, boxShadow: 1 }}>
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                bgcolor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 1,
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                Parse Data import
              </Typography>
              {hasUnlinkedParseFinancialRows && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#b45309', fontSize: 12 }}>
                  Financial rows are not linked to Parse Data yet. Select Parse Data suppliers and click Re-import, then save.
                </Typography>
              )}
              {selectedParseSuppliers.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#4b5563', fontSize: 12 }}>
                  {selectedParseSuppliers.length} linked Parse Data supplier(s) shown below. Change selection and click Re-import to refresh financial rows.
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <Box sx={{ flex: '1 1 320px', minWidth: 240 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  open={parseSupplierOpen}
                  onOpen={() => setParseSupplierOpen(true)}
                  onClose={(_e, reason) => {
                    if (reason === 'selectOption') return;
                    setParseSupplierOpen(false);
                  }}
                  options={parseSupplierAutocompleteOptions}
                  value={selectedParseSuppliers}
                  inputValue={parseSupplierInputValue}
                  loading={parseSupplierLoading}
                  readOnly={!editMode}
                  disabled={parseFinancialLoading}
                  getOptionLabel={(option) =>
                    option.supplierName && option.supplierName !== option.supplierIFA
                      ? `${option.supplierName} (${option.supplierIFA})`
                      : option.supplierIFA
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.supplierIFA.toLowerCase() === value.supplierIFA.toLowerCase()
                  }
                  onInputChange={(_e, value, reason) => {
                    if (reason === 'blur') return;
                    if (reason === 'reset') {
                      setParseSupplierInputValue('');
                      setParseSupplierOpen(true);
                      return;
                    }
                    if (reason === 'input') {
                      if (
                        parseSupplierListSearchRef.current &&
                        value.trim() !== parseSupplierListSearchRef.current
                      ) {
                        setParseSupplierOpen(false);
                        parseSupplierListSearchRef.current = '';
                      }
                      setParseSupplierInputValue(value);
                      scheduleParseSupplierSearch(value);
                    }
                  }}
                  noOptionsText={
                    parseSupplierLoading
                      ? "Searching..."
                      : parseSupplierInputValue.trim().length < PARSE_DATA_MIN_SEARCH_LENGTH
                        ? "Type 2+ characters"
                        : `No match for "${parseSupplierInputValue.trim()}"`
                  }
                  onChange={(_e, value) => {
                    handleParseSuppliersChange(value);
                    setParseSupplierOpen(true);
                  }}
                  filterOptions={(x) => x}
                  limitTags={3}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      const label =
                        option.supplierName && option.supplierName !== option.supplierIFA
                          ? `${option.supplierName} (${option.supplierIFA})`
                          : option.supplierIFA;
                      return (
                        <Box
                          key={key}
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            bgcolor: '#ede9fe',
                            color: '#5b21b6',
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            m: 0.25,
                            fontSize: 12,
                            fontWeight: 500,
                            maxWidth: '100%',
                          }}
                          {...tagProps}
                        >
                          {label}
                        </Box>
                      );
                    })
                  }
                  ListboxProps={{ style: { maxHeight: 280 } }}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parse Data suppliers"
                      size="small"
                      placeholder="Search and select suppliers"
                      sx={{
                        bgcolor: '#fff',
                        '& .MuiOutlinedInput-root': { minHeight: 40 },
                        '& .MuiOutlinedInput-input': { py: 0.5, fontSize: 13 },
                        '& .MuiInputLabel-root': { fontSize: 13 },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {parseSupplierLoading ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disablePortal
                />
                </Box>
                <SecondaryButton
                  size="small"
                  disabled={!editMode || selectedParseSuppliers.length === 0 || parseFinancialLoading}
                  onClick={() => handleFetchParseData()}
                  startIcon={
                    parseFinancialLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={{
                    height: 40,
                    minWidth: 118,
                    fontSize: 13,
                    px: 2,
                    '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#9ca3af', bgcolor: '#f9fafb' },
                  }}
                >
                  {hasFinancialRowData(financialRows) ? 'Re-import' : 'Import'}
                </SecondaryButton>
              </Box>
            </Box>

            <FinancialDetailsTable
              rows={financialRows}
              onRowsChange={setFinancialRows}
              editMode={editMode}
              parseDataOnly
              filterResetKey={financialFilterResetKey}
              commodityOptions={commodityOptions}
              onCreateCommodity={createCommodityOption}
            />
          </Paper>
        )}
        {/* Additional Collapsible Sections */}
        {renderCollapsibleSection(
          "Commodity Details",
          expandedHeader,
          setExpandedHeader,
          <Paper elevation={0} sx={{ p: 2, background: '#f9fafb', borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600, fontSize: 13 }}>
              Add Commodity
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
              <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
                <Autocomplete
                  freeSolo
                  options={financialCommodityOptions}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.commodity || '')}
                  filterSelectedOptions
                  value={selectedCommodity}
                  inputValue={commodityInput}
                  onInputChange={(_e, newInput) => setCommodityInput(newInput)}
                  onChange={(_e, newValue) => setSelectedCommodity(newValue)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Commodity"
                      size="small"
                      sx={{
                        bgcolor: '#fff',
                        '& .MuiOutlinedInput-root': { height: 40 },
                        '& .MuiOutlinedInput-input': { py: 0, fontSize: 13 },
                        '& .MuiInputLabel-root': { fontSize: 13 },
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
                <TextField
                  select
                  label="Commodity Strategy (optional)"
                  size="small"
                  fullWidth
                  value={selectedPreferenceStatus?.id ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setSelectedPreferenceStatus(null);
                      return;
                    }
                    const id = Number(raw);
                    setSelectedPreferenceStatus(preferenceStatusOptions.find((s) => s.id === id) ?? null);
                  }}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-root': { height: 40 },
                    '& .MuiOutlinedInput-input': { py: 0, fontSize: 13 },
                    '& .MuiInputLabel-root': { fontSize: 13 },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {preferenceStatusOptions.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.status}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <PrimaryButton
                size="small"
                onClick={handleAddSelectedCommodity}
                disabled={
                  !selectedCommodity ||
                  selectedCommodities.some((c) =>
                    isCommodityObject(c) && isCommodityObject(selectedCommodity)
                      ? c.commodityID === selectedCommodity.commodityID
                      : c === selectedCommodity
                  )
                }
                sx={{ height: 40, px: 2 }}
              >
                Add
              </PrimaryButton>
            </Box>

            <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>Commodity</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: 220 }}>Commodity Strategy</TableCell>
                    <TableCell sx={{ width: 40, py: 0.75 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedCommodities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 1.5, fontSize: 13, color: 'text.secondary' }}>
                        No commodities added.
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedCommodities.map((item: any) => (
                      <TableRow key={String(commodityKey(item))} hover>
                        <TableCell sx={{ py: 0.5, fontSize: 13 }}>
                          {isCommodityObject(item) ? item.commodity : item}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={
                              isCommodityObject(item)
                                ? (normalizePreferenceStatusId(item.preferenceStatusID) ?? '')
                                : ''
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              handleCommodityPreferenceChange(item, raw === '' ? 0 : Number(raw));
                            }}
                            SelectProps={{ displayEmpty: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': { height: 32, bgcolor: '#fff' },
                              '& .MuiOutlinedInput-input': { py: 0, fontSize: 13 },
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {preferenceStatusOptions.map((status) => (
                              <MenuItem key={status.id} value={status.id}>
                                {status.status}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 0.5 }}>
                          {(showUpdateButton || editMode) && (
                            <DeleteIconButton
                              label="Remove commodity"
                              onClick={() => { setOpen(true); setConfirmTarget(item); }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              aria-labelledby="confirm-dialog-title"
              aria-describedby="confirm-dialog-desc"
            >
              <DialogTitle id="confirm-dialog-title" sx={{ color: '#8A00E5' }}><strong>Confirm Action</strong></DialogTitle>
              <DialogContent id="confirm-dialog-desc">
                {confirmTarget != null && (
                  <p style={{ marginTop: 0 }}>
                    Commodity: <strong>{commodityLabel(confirmTarget)}</strong>
                  </p>
                )}
                <p style={{ marginBottom: 0 }}>
                  If you delete this commodity, the details associated with the selected commodity under <strong>Product Portfolio</strong> and
                  <strong> Contact Details</strong> will be cleared as well.
                  <br />
                  Are you sure you want to proceed?
                </p>
              </DialogContent>
              <DialogActions>
                <PrimaryButton onClick={() => setOpen(false)}>
                  No
                </PrimaryButton>
                <DangerButton
                  onClick={() =>
                    handleDeleteCommodity(confirmTarget!, {
                      closeConfirm: () => setOpen(false),
                    })
                  }
                >
                  Yes
                </DangerButton>
              </DialogActions>
            </Dialog>
          </Paper>
        )}

        {renderCollapsibleSection(
          "Product Portfolio",
          expandedHeader,
          setExpandedHeader,
          <Paper elevation={0} sx={{ p: 2, background: '#f9fafb', borderRadius: 2, boxShadow: 1 }}>
            <TextField
              label="Portfolio Details"
              fullWidth
              size="small"
              sx={{
                mb: 2,
                bgcolor: '#fff',
                '& .MuiOutlinedInput-root': { height: 40 },
                '& .MuiOutlinedInput-input': { py: 0, fontSize: 13 },
                '& .MuiInputLabel-root': { fontSize: 13 },
              }}
              value={details}
              onChange={(e) => setportfolioDeatils(e.target.value)}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600, fontSize: 13 }}>
                Products & Production Locations
              </Typography>
              <PrimaryButton
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenPortfolioAddDialog}
                sx={{ fontSize: 13 }}
              >
                Add Products
              </PrimaryButton>
            </Box>

            <Dialog
              open={portfolioAddDialogOpen}
              onClose={handleClosePortfolioAddDialog}
              fullWidth
              maxWidth="sm"
              aria-labelledby="portfolio-add-dialog-title"
            >
              <DialogTitle
                id="portfolio-add-dialog-title"
                sx={{ color: '#8A00E5', fontWeight: 600, fontSize: 18, pb: 1 }}
              >
                Add Products to Portfolio
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Autocomplete
                    options={selectedCommodities}
                    getOptionLabel={(option) =>
                      isCommodityObject(option) ? option.commodity ?? '' : String(option ?? '')
                    }
                    isOptionEqualToValue={(option, value) => commodityKey(option) === commodityKey(value)}
                    value={selectedProductCommodity}
                    onChange={(_e, newValue) => setSelectedProductCommodity(newValue)}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Commodity"
                        size="small"
                        required
                        sx={{
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-root': { minHeight: 40 },
                          '& .MuiInputLabel-root': { fontSize: 13 },
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    options={countryOptions}
                    getOptionLabel={(option) => option.longName || ''}
                    getOptionKey={(option) => `${option?.countryID ?? ''}_${option?.longName ?? ''}`}
                    value={selectedCountries}
                    onChange={(_e, newValue) => setSelectedCountries(newValue)}
                    fullWidth
                    limitTags={3}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.longName || option}
                            size="small"
                            {...tagProps}
                            sx={{ maxWidth: 160 }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Production Locations"
                        size="small"
                        sx={{
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-root': { minHeight: 40 },
                          '& .MuiInputLabel-root': { fontSize: 13 },
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    fullWidth
                    options={productOptions}
                    getOptionLabel={getProductOptionLabel}
                    isOptionEqualToValue={isSameProductOption}
                    value={selectedProducts}
                    onChange={(_e, newValue) => setSelectedProducts(newValue)}
                    disabled={!selectedProductCommodity}
                    limitTags={4}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Products"
                        size="small"
                        required
                        placeholder={
                          selectedProductCommodity
                            ? 'Select one or more products'
                            : 'Select a commodity first'
                        }
                        sx={{
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-root': { minHeight: 40 },
                          '& .MuiInputLabel-root': { fontSize: 13 },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={getProductOptionLabel(option)}
                            size="small"
                            {...tagProps}
                            sx={{ maxWidth: 200 }}
                          />
                        );
                      })
                    }
                  />

                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.75, display: 'block' }}>
                      Lead Time (optional)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        label="Months"
                        size="small"
                        fullWidth
                        type="number"
                        value={selectedLeadTimeMonths}
                        onChange={(e) => setSelectedLeadTimeMonths(e.target.value)}
                        disabled={!selectedProductCommodity}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ bgcolor: '#fff' }}
                      />
                      <TextField
                        label="Weeks"
                        size="small"
                        fullWidth
                        type="number"
                        value={selectedLeadTimeWeeks}
                        onChange={(e) => setSelectedLeadTimeWeeks(e.target.value)}
                        disabled={!selectedProductCommodity}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ bgcolor: '#fff' }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Create new product"
                      size="small"
                      fullWidth
                      value={newProduct}
                      onChange={(e) => setNewProduct(e.target.value)}
                      disabled={!selectedProductCommodity}
                      sx={{ bgcolor: '#fff' }}
                    />
                    <SecondaryButton
                      size="small"
                      onClick={() => { void handleAddProductOption(); }}
                      disabled={!canAddNewProductOption || !selectedProductCommodity}
                      sx={{ mt: 0.25, height: 40, whiteSpace: 'nowrap' }}
                    >
                      New Product
                    </SecondaryButton>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <SecondaryButton onClick={handleClosePortfolioAddDialog}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleAddProductPortfolio}
                  disabled={!selectedProductCommodity || selectedProducts.length === 0}
                >
                  Add to Portfolio
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            <Dialog
              open={portfolioEditDialogOpen}
              onClose={handleClosePortfolioEditDialog}
              fullWidth
              maxWidth="sm"
              aria-labelledby="portfolio-edit-dialog-title"
            >
              <DialogTitle
                id="portfolio-edit-dialog-title"
                sx={{ color: '#8A00E5', fontWeight: 600, fontSize: 18, pb: 1 }}
              >
                Edit Portfolio Product
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                  <Autocomplete
                    options={
                      editPortfolioCommodity &&
                      !selectedCommodities.some(
                        (c) => commodityKey(c) === commodityKey(editPortfolioCommodity)
                      )
                        ? [editPortfolioCommodity, ...selectedCommodities]
                        : selectedCommodities
                    }
                    getOptionLabel={(option) =>
                      isCommodityObject(option) ? option.commodity ?? '' : String(option ?? '')
                    }
                    isOptionEqualToValue={(option, value) => commodityKey(option) === commodityKey(value)}
                    value={editPortfolioCommodity}
                    onChange={(_e, newValue) => setEditPortfolioCommodity(newValue)}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Commodity"
                        size="small"
                        required
                        sx={{
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-root': { minHeight: 40 },
                          '& .MuiInputLabel-root': { fontSize: 13 },
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    options={
                      editPortfolioProduct &&
                      !productOptions.some((p) => isSameProductOption(p, editPortfolioProduct))
                        ? [editPortfolioProduct, ...productOptions]
                        : productOptions
                    }
                    getOptionLabel={getProductOptionLabel}
                    isOptionEqualToValue={isSameProductOption}
                    value={editPortfolioProduct}
                    onChange={(_e, newValue) => setEditPortfolioProduct(newValue)}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        size="small"
                        required
                        sx={{
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-root': { minHeight: 40 },
                          '& .MuiInputLabel-root': { fontSize: 13 },
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    options={countryOptions}
                    getOptionLabel={(option) => option.longName || ''}
                    getOptionKey={(option) => `${option?.countryID ?? ''}_${option?.longName ?? ''}`}
                    value={editPortfolioCountries}
                    onChange={(_e, newValue) => setEditPortfolioCountries(newValue)}
                    fullWidth
                    limitTags={3}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.longName || option}
                            size="small"
                            {...tagProps}
                            sx={{ maxWidth: 160 }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Production Locations"
                        size="small"
                        sx={{
                          bgcolor: '#fff',
                          '& .MuiOutlinedInput-root': { minHeight: 40 },
                          '& .MuiInputLabel-root': { fontSize: 13 },
                        }}
                      />
                    )}
                  />

                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.75, display: 'block' }}>
                      Lead Time (optional)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        label="Months"
                        size="small"
                        fullWidth
                        type="number"
                        value={editLeadTimeMonths}
                        onChange={(e) => setEditLeadTimeMonths(e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ bgcolor: '#fff' }}
                      />
                      <TextField
                        label="Weeks"
                        size="small"
                        fullWidth
                        type="number"
                        value={editLeadTimeWeeks}
                        onChange={(e) => setEditLeadTimeWeeks(e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ bgcolor: '#fff' }}
                      />
                    </Box>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <SecondaryButton onClick={handleClosePortfolioEditDialog}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleSavePortfolioEdit}
                  disabled={!editPortfolioCommodity || !editPortfolioProduct}
                >
                  Save Changes
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: '22%' }}>Commodity</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>Locations</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: 140 }}>Lead Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, width: 72, px: 0.5 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productPortfolio.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 1.5, fontSize: 13, color: 'text.secondary' }}>
                        No products added.
                      </TableCell>
                    </TableRow>
                  ) : (
                    productPortfolio.map((item, idx) => {
                      const leadMonths = formatLeadTimeValue(item.leadTimeMonths);
                      const leadWeeks = formatLeadTimeValue(item.leadTimeWeeks);
                      const leadTimeLabel =
                        leadMonths || leadWeeks
                          ? [leadMonths ? `${leadMonths} mo` : null, leadWeeks ? `${leadWeeks} wk` : null]
                              .filter(Boolean)
                              .join(', ')
                          : '—';

                      return (
                      <TableRow key={`${getProductOptionId(item.product)}_${getPortfolioCommodityId(item.productCommodity)}_${idx}`} hover>
                        <TableCell sx={{ py: 0.75, fontSize: 13, fontWeight: 500 }}>
                          {item.product?.portfolio
                            ? item.product.portfolio
                            : typeof item.product === 'string'
                              ? item.product
                              : getProductOptionLabel(item.product)}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: 13, color: '#8A00E5' }}>
                          {item.productCommodity
                            ? isCommodityObject(item.productCommodity)
                              ? item.productCommodity.commodity
                              : item.productCommodity
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: 12, color: 'text.secondary' }}>
                          {item.countries?.length
                            ? item.countries.map((c) => c.longName || c).join(', ')
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: 13, color: 'text.secondary' }}>
                          {leadTimeLabel}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 0.5, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {(showUpdateButton || editMode) && (
                            <>
                              <EditIconButton
                                label="Edit product"
                                onClick={() => handleOpenPortfolioEditDialog(idx)}
                              />
                              <DeleteIconButton
                                label="Remove product"
                                onClick={() => handleRemoveProductPortfolio(idx)}
                              />
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {renderCollapsibleSection(
          "Supplier Details",
          expandedHeader,
          setExpandedHeader,
          <Paper elevation={0} sx={{ p: 2, background: '#f9fafb', borderRadius: 3, boxShadow: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 500, letterSpacing: 1 }}>
              Supplier Classification
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Box>
                <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                  <Autocomplete
                    multiple
                    options={regionOptions.filter(
                      (option) => !selectedRegions.some(selected => selected.region === option.region)
                    )}
                    getOptionLabel={(option) => option.region || ''}
                    getOptionKey={(option) => `${option?.regionID ?? ''}_${option?.region ?? ''}`}
                    value={selectedRegions}
                    onChange={(_e, newValue) => setSelectedRegions(newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              bgcolor: '#e3e6ea',
                              borderRadius: 1.5,
                              px: 1,
                              py: 0.1,
                              m: 0.2,
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#333',
                              minHeight: 20,
                              maxWidth: 120,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            {...tagProps}
                          >
                            <span style={{ marginRight: 4 }}>{option.region}</span>
                            {(showUpdateButton || editMode) && <CloseIcon

                              fontSize="inherit"
                              style={{ fontSize: 13, cursor: 'pointer' }}
                              onClick={tagProps.onDelete} // ✅ Attach delete handler
                            />}
                          </Box>
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Select Region" size="small" sx={{ bgcolor: '#fff', borderRadius: 1, height: 32, fontSize: 13, '.MuiInputBase-input': { py: 0.5, fontSize: 13 } }} />
                    )}
                    sx={{ flexGrow: 1, minHeight: 32, maxWidth: '52%' }}
                  />
                </Box>
              </Box>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2.5, color: 'primary.main', fontWeight: 500, letterSpacing: 1 }}>
              Contract Type
            </Typography>
            <Box display="flex" flexDirection="row" gap={2} mt={0.5}>
              <FormControlLabel
                control={<Checkbox size="small" checked={contractTypes.masterPurchasingAgreementMPA} onChange={e => setContractTypes(ct => ({ ...ct, masterPurchasingAgreementMPA: e.target.checked }))}  />}
                label="Master Purchasing Agreement (MPA)"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={contractTypes.nonDisclosureAgreement} onChange={e => setContractTypes(ct => ({ ...ct, nonDisclosureAgreement: e.target.checked }))}  />}
                label="Non-disclosure agreement (NDA)"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={contractTypes.project} onChange={e => setContractTypes(ct => ({ ...ct, project: e.target.checked }))}  />}
                label="Project"
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, color: 'primary.main', fontWeight: 500, letterSpacing: 1 }}>
              Terms & Conditions
            </Typography>
            <Box display="flex" flexDirection="row" gap={2} mt={1} alignItems="flex-start">
              <TextField
                type="number"
                label="Payment Terms(Days)"
                value={paymentTerms}
                //nov17
                onChange={e => setPaymentTerms(Math.max(0, (e.target as HTMLInputElement).valueAsNumber))}
                size="small"
                sx={{
                  maxWidth: 220,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  height: 40,
                  '.MuiInputBase-root': { minHeight: 40 },
                  '.MuiInputBase-input': { py: 0.5, fontSize: 13, minHeight: 24 }
                }}
                InputProps={{ sx: { height: 40, minHeight: 40 } }}
              />
              <Autocomplete
                options={fTermsOptions}
                getOptionLabel={(option) => option.name || ''}
                getOptionKey={(option) => `${option?.id ?? ''}_${option?.name ?? ''}`}
                value={selectedFTerm}
                //inputValue={newFTerm}
                // onInputChange={(_e, newInput) => setNewFTerm(newInput)}
                onChange={(_e, newValue) => setSelectedFTerm(newValue)}
                renderTags={(value, getTagProps) =>
                  Array.isArray(value)
                    ? value.map((option, index) => (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: '#e3e6ea',
                          borderRadius: 1.5,
                          px: 1,
                          py: 0.1,
                          m: 0.2,
                          fontSize: 11,
                          fontWeight: 500,
                          color: '#333',
                          minHeight: 20,
                          maxWidth: 120,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        {...getTagProps({ index })}
                      >
                        <span style={{ marginRight: 2 }}>{option.name}</span>
                        <CloseIcon fontSize="inherit" style={{ fontSize: 13, cursor: 'pointer' }} />
                      </Box>
                    ))
                    : null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="F-Terms"
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: 1,
                      height: 40,
                      '.MuiInputBase-root': { minHeight: 40 },
                      '.MuiInputBase-input': { py: 0.5, fontSize: 13, minHeight: 24 }
                    }}
                    InputProps={{ ...params.InputProps, sx: { height: 40, minHeight: 40 } }}
                  />
                )}
                sx={{ flex: 1, minWidth: 180, maxWidth: 220, minHeight: 40 }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, color: 'primary.main', fontWeight: 500, letterSpacing: 1 }}>
              MPA
            </Typography>
            <Box display="flex" flexDirection="row" gap={2} mt={1} alignItems="flex-start">
              <TextField
                label="Link to MPA (if available)"
                value={mpaLink}
                title="Double click to open the link"
                onChange={(e) => setMpaLink(e.target.value)}
                size="small"
                sx={{
                  maxWidth: 350,
                  width: '35%',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  height: 40,
                  '.MuiInputBase-root': { minHeight: 40 },
                  '.MuiInputBase-input': { py: 0.5, fontSize: 13, minHeight: 24 },
                  marginBottom: 2
                }}
                InputProps={{
                  sx: { height: 40, minHeight: 40 },
                  style: {
                    color: isValidUrl(mpaLink)
                      ? editMode
                        ? '#8A00E5' // Darker purple in edit mode
                        : '#bc6cf2ff' // Lighter purple in view mode
                      : 'inherit',
                    textDecoration: isValidUrl(mpaLink) ? 'underline' : 'none',
                    cursor: isValidUrl(mpaLink) ? 'pointer' : 'default'
                  },
                  onDoubleClick: () => {
                    if (isValidUrl(mpaLink)) {
                      window.open(mpaLink, '_blank');
                    }
                  }
                }}
                inputProps={{
                  style: {
                    cursor: isValidUrl(mpaLink) ? 'pointer' : 'default'
                  }
                }}
                error={mpaLink.length > 0 && !isValidUrl(mpaLink)}
                helperText={
                  mpaLink.length > 0 && !isValidUrl(mpaLink)
                    ? 'Please enter a valid URL (e.g., https://example.com)'
                    : ''
                }

              />
              <Autocomplete
                size="small"
                options={statusOptions}
                getOptionLabel={(option) => option.name || ''}
                getOptionKey={(option) => `${option?.id ?? ''}_${option?.name ?? ''}`}
                value={selectedStatus}
                // inputValue={newStatus}
                // onInputChange={(_e, newInput) => setNewStatus(newInput)}
                onChange={(_e, newValue) => setSelectedStatus(newValue)}
                renderTags={(value, getTagProps) =>
                  Array.isArray(value)
                    ? value.map((option, index) => (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: '#e3e6ea',
                          borderRadius: 1.5,
                          px: 1,
                          py: 0.1,
                          m: 0.2,

                          fontSize: 11,
                          fontWeight: 500,
                          color: '#333',
                          minHeight: 20,
                          maxWidth: 120,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        {...getTagProps({ index })}
                      >
                        <span style={{ marginRight: 3 }}>{option.name}</span>
                        <CloseIcon fontSize="inherit" style={{ fontSize: 13, cursor: 'pointer' }} />
                      </Box>
                    ))
                    : null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="EAD Supplier Strategic Status"
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: 1,
                      height: 40,
                      '.MuiInputBase-root': { minHeight: 40 },
                      '.MuiInputBase-input': { py: 0.5, fontSize: 13, minHeight: 24 }
                    }}
                    InputProps={{ ...params.InputProps, sx: { height: 40, minHeight: 40 } }}
                  />
                )}
                sx={{ flex: 1, minWidth: 265, maxWidth: 220, minHeight: 40, }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 1.5, color: 'primary.main', fontWeight: 500, letterSpacing: 1 }}>
              Supplier Qualification Status
            </Typography>
            <Box display="flex" flexDirection="row" gap={2} mt={0.5}>
              <FormControlLabel
                control={<Checkbox size="small" checked={qualificationStatus.qualificationStatus_NotQualified} onChange={e => setQualificationStatus(qs => ({ ...qs, qualificationStatus_NotQualified: e.target.checked }))}  />}
                label="Not Qualified"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={qualificationStatus.qualificationStatus_ReadyforBusiness_R4B} onChange={e => setQualificationStatus(qs => ({ ...qs, qualificationStatus_ReadyforBusiness_R4B: e.target.checked }))}  />}
                label="Ready for Business (R4B)"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={qualificationStatus.qualificationStatus_ReadytoOrder_R4O} onChange={e => setQualificationStatus(qs => ({ ...qs, qualificationStatus_ReadytoOrder_R4O: e.target.checked }))}  />}
                label="Ready to Order (R2O)"
              />
            </Box>
          </Paper>
        )}

        {renderCollapsibleSection(
          "EAD Integrated Offerings",
          expandedHeader,
          setExpandedHeader,
          //changedbymetoday(nov14)
          <Paper elevation={0} sx={{ p: 2, background: '#f9fafb', borderRadius: 3, boxShadow: 1, overflow: 'visible', display: 'flex', flexWrap: 'wrap', paddingBottom: 3 }}>
            <Autocomplete
              multiple
              options={integratedOfferingsOptions.filter(
                (option) => !selectedIntegratedOfferings.some(selected => selected.name === option.name)
              )}
              getOptionLabel={(option) => option.name || ''}
              getOptionKey={(option) => `${option?.id ?? ''}_${option?.name ?? ''}`}
              value={selectedIntegratedOfferings}
              //inputValue={newIntegratedOffering}
              //onInputChange={(_e, newInput) => setNewIntegratedOffering(newInput)}
              onChange={(_e, newValue) => setSelectedIntegratedOfferings(newValue)}
              //changedbyme(14nov)
              renderTags={(value, getTagProps) =>
                Array.isArray(value)
                  ? value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: '#e3e6ea',
                          borderRadius: 1.5,
                          px: 1,
                          py: 0.1,
                          m: 0.2,
                          fontSize: 11,
                          fontWeight: 500,
                          color: '#333',
                          minHeight: 20,
                          maxWidth: 120,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        {...tagProps}
                      >
                        <span style={{ marginRight: 2 }}>{option.name}</span>
                        {(showUpdateButton || editMode) && <CloseIcon
                          fontSize="inherit"
                          style={{ fontSize: 13, cursor: 'pointer' }}
                          onClick={tagProps.onDelete}
                        />}
                      </Box>
                    );
                  })
                  : null
              }

              renderInput={(params) => (
                <TextField {...params} label="Integrated Offerings" size="small" sx={{ bgcolor: '#fff', borderRadius: 1, height: 32, fontSize: 13, minWidth: 70, '.MuiInputBase-input': { py: 0.5, fontSize: 13, } }} />
              )}
              sx={{ flexGrow: 1, minWidth: 200, width: 'auto' }}
            />
          </Paper>
        )}


        {renderCollapsibleSection(
          "Contact Details",
          expandedHeader,
          setExpandedHeader,
          <Paper elevation={0} sx={{ p: 1, background: '#fff', borderRadius: 1 }}>
            {contacts.map((contact, index) => (
              <Grid container spacing={1} key={index} alignItems="center" mb={0.5}>
                <Box mt={0.5} display="flex" gap={1}>
                  <TextField
                    label="Name"
                    value={contact.name}
                    onChange={e => handleContactChange(index, "name", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Surname"
                    value={contact.surname}
                    onChange={e => handleContactChange(index, "surname", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="E-mail"
                    value={contact.email}
                    onChange={(e) => handleContactChange(index, "email", e.target.value)}
                    fullWidth
                    size="small"
                    error={!isValidEmail(contact.email)}
                    helperText={
                      !isValidEmail(contact.email)
                        ? "Please enter a valid email address"
                        : undefined // ✅ Avoid empty string
                    }

                  />

                  <TextField
                    label="Job role"
                    value={contact.jobRole || ""}
                    onChange={e => handleContactChange(index, "jobRole", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Autocomplete
                    multiple
                    options={selectedCommodities}
                    getOptionLabel={(option) =>
                      isCommodityObject(option) ? option.commodity : option
                    }
                    getOptionKey={(option) =>
                      isCommodityObject(option)
                        ? `${option?.commodityID ?? ''}_${option?.commodity ?? ''}`
                        : String(option)
                    }
                    value={contact.commodity || []}
                    onChange={(_e, newValue) => handleContactChange(index, "commodity", newValue)}

                    /* UI-only */
                    limitTags={3}
                    filterSelectedOptions
                    disablePortal
                    slotProps={{
                      popper: { sx: { width: '100%' } },
                      paper: { sx: { maxHeight: 300, overflow: 'auto', borderRadius: 1 } },
                      listbox: {
                        sx: {
                          p: 0.5,
                          '& li': { whiteSpace: 'normal', wordBreak: 'break-word' },
                        },
                      },
                    }}

                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        const label = isCommodityObject(option) ? option.commodity : String(option);

                        return (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              bgcolor: "#e3e6ea",
                              borderRadius: 1.5,
                              px: 1,
                              py: 0.3,             // same vertical padding as regions for equal height
                              m: 0.2,
                              fontSize: 11,
                              fontWeight: 500,
                              color: "#333",
                              minHeight: 24,       // ❗ match tag height with regions
                              maxWidth: '100%',
                              whiteSpace: "normal", // wrap long text
                              wordBreak: "break-word",
                              lineHeight: 1.2,
                              gap: 4,
                            }}
                            {...tagProps}
                            title={label}
                          >
                            <span>{label}</span>
                            {(showUpdateButton || editMode) ? (
                              <CloseIcon
                                fontSize="inherit"
                                style={{ fontSize: 13, cursor: "pointer" }}
                                onClick={tagProps.onDelete}
                              />
                            ) : null}
                          </Box>
                        );
                      })
                    }

                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Commodity"
                        size="small"
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 1,
                          minWidth: 200,
                          ".MuiInputBase-root": {
                            minHeight: 36,        // ❗ input box height
                            alignItems: "flex-start",
                            flexWrap: "wrap",     // allow tags to wrap to next line
                            gap: 0.5,
                          },
                          ".MuiChip-root": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          },
                        }}
                      />
                    )}

                    sx={{
                      minWidth: 200,
                    }}
                  />
                  <Autocomplete
                    multiple
                    options={contactRegionOptions.filter(
                      (option) =>
                        !(contact.regions || []).some((selected) => (selected as any).region === (option as any).region)
                    )}
                    getOptionLabel={option => option.region || ''}
                    getOptionKey={(option) => `${(option as any)?.regionID ?? ''}_${(option as any)?.region ?? ''}`}
                    value={contact.regions || []}
                    // inputValue={contact.newRegion || ""}
                    // onInputChange={(_e, newInput) => handleContactChange(index, "newRegion", newInput)}
                    onChange={(_e, newValue) => handleContactChange(index, "regions", newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, idx) => {
                        const tagProps = getTagProps({ index: idx });
                        return (
                          <Box
                            //17nov
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              bgcolor: '#e3e6ea',
                              borderRadius: 1.5,
                              px: 1,
                              py: 0.1,
                              m: 0.2,
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#333',
                              minHeight: 20,
                              maxHeight: 10,
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            {...tagProps}
                          >
                            <span style={{ marginRight: 4 }}>{option.region}</span>
                            {(showUpdateButton || editMode) && <CloseIcon
                              fontSize="inherit"
                              style={{ fontSize: 15, cursor: 'pointer' }}
                              onClick={tagProps.onDelete}
                            />}
                          </Box>
                        );
                      })
                    }
                    renderInput={params => (
                      <TextField {...params} label="Region" size="small" sx={{ bgcolor: '#fff', borderRadius: 1, minWidth: 140 }} />
                    )}
                    sx={{ flex: 1, minHeight: 32 }}
                  />
                  {((showUpdateButton || editMode) &&
                    <DeleteIconButton label="Remove contact" sx={{ top: !isValidEmail(contact.email) ? '-22px' : '0px' }} onClick={() => handleDeleteContactRow(index)} />
                  )}
                </Box>
              </Grid>
            ))}
            {(showUpdateButton || editMode) && (
              <SecondaryButton
                size="small"
                onClick={handleAddContact}
                sx={{ mt: 1, maxWidth: 180 }}
              >
                Add Contact
              </SecondaryButton>
            )}
          </Paper>
        )}

        </Box>

        {/* Footer Save/Update Button aligned right */}
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            bottom: 0,
            width: '98%',
            bgcolor: 'background.paper',
            boxShadow: 3,
            py: 1.5,
            px: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 1200,
          }}
        >
          {saving ? (
            <PrimaryButton
              size="small"
              startIcon={<SaveIcon fontSize="small" />}
              disabled
            >
              Saving...
            </PrimaryButton>
          ) : !editMode ? (

            <Box sx={{ display: 'flex', gap: 1 }}>
              <DangerOutlineButton
                startIcon={<DeleteIcon fontSize="small" />}
                size="small"
                onClick={() => setdialogboxopen(true)}
              >
                Delete Supplier
              </DangerOutlineButton>

              {/* Confirmation dialog */}
              <Dialog
                open={dialogboxopen}
                onClose={() => setdialogboxopen(false)}
                aria-labelledby="confirm-dialog-titles"
                aria-describedby="confirm-dialog-description"
              >
                <DialogTitle id="confirm-dialog-titles" sx={{ color: '#8A00E5' }}>
                  <strong>Confirm Deletion</strong>
                </DialogTitle>

                <DialogContent id="confirm-dialog-description">
                  <p style={{ margin: 0 }}>
                    Are you sure you want to delete the supplier?
                  </p>
                </DialogContent>

                <DialogActions>
                  <PrimaryButton onClick={() => setdialogboxopen(false)}>
                    No
                  </PrimaryButton>

                  <DangerButton
                    onClick={() => {
                      handleDeleteSupplier();  // call your delete handler
                      setdialogboxopen(false);          // close dialog after confirmation
                    }}
                  >
                    Yes, Delete
                  </DangerButton>
                </DialogActions>
              </Dialog>
              <SecondaryButton
                startIcon={<EditIcon fontSize="small" />}
                size="small"
                onClick={handleEnterEditMode}
              >
                Edit
              </SecondaryButton>
            </Box>
          ) : showUpdateButton ? (
            <PrimaryButton
              size="small"
              onClick={handleSaveSupplier}
            >
              Update
            </PrimaryButton>
          ) : (
            <PrimaryButton
              startIcon={<SaveIcon fontSize="small" />}
              size="small"
              onClick={handleSaveSupplier}
              sx={{ cursor: isFormValid() ? 'pointer' : 'not-allowed' }}
            >
              {saving ? 'Saving...' : 'Save'}
            </PrimaryButton>
          )}
        </Box>
      </Container>
    </React.Fragment >
  );
};

// 🔁 Utility to render collapsible sections with icon and color differentiation
const renderCollapsibleSection = (
  title: string,
  expandedHeader: string | null,
  setExpandedHeader: React.Dispatch<React.SetStateAction<string | null>>,
  children: React.ReactNode
) => {
  const isOpen = expandedHeader === title;
  return (
    <Box mb={1}>
      <Paper
        elevation={1}
        onClick={() => setExpandedHeader((prev) => (prev === title ? null : title))}
        sx={{
          p: 1.5,
          cursor: "pointer",
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          background: isOpen ? '#e0e0e0' : '#f5f5f5',
          transition: 'background 0.2s',

        }}
      >
        {isOpen ? (
          <ExpandMoreIcon sx={{ color: '#666', mr: 1, transition: 'color 0.2s' }} />
        ) : (
          <ChevronRightIcon sx={{ color: '#888', mr: 1, transition: 'color 0.2s' }} />
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: isOpen ? '#222' : 'inherit',
            letterSpacing: 1,
            transition: 'color 0.2s',
          }}
        >
          {title}
        </Typography>
      </Paper>
      <Collapse in={isOpen}>
        <Box sx={{ mt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

function isCommodityObject(obj: any): obj is {
  commodityID?: any;
  commodity?: string;
  preferenceStatusID?: number | null;
  preferenceStatus?: string | null;
} {
  return obj && (typeof obj === 'object') && ('commodity' in obj || 'commodityID' in obj);
}

// Wrap App with AuthProvider
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;